import * as THREE from 'three';
import { StorageBufferAttribute } from 'three/webgpu';
import { storage, uniform, workgroupId, localId, wgslFn } from './tslCompat';
import {
  GPULBVHBuilder,
  LBVHSorterType,
} from '../../lvbh';
import type { PhysicsSubmissionLedger } from './physicsSubmissionLedger';
import type { CompactContactPoolLayout } from './compactContactPool';
import {
  SLEEPING_BODY_STATE_SLEEPING_BIT,
  type SleepingSparseBroadphaseCertificate,
} from './sleepingIslands';
import {
  CONSTRAINT_SAFETY_BROADPHASE_FATAL_BIT,
  CONSTRAINT_SAFETY_CCD_FATAL_BIT,
  CONSTRAINT_SAFETY_CONTACT_FATAL_BIT,
  normalizeConstraintSafetyReadbackTimeoutMs,
} from './constraintSafety';
import {
  createThreeWebGpuComputePreparationCache,
  isThreeWebGpuComputeBridgeReady,
  prepareThreeWebGpuCompute,
  recordPreparedThreeWebGpuCompute,
  type NativeComputePassDescriptorFactory,
  type PreparedThreeWebGpuCompute,
  type ThreeWebGpuComputeCommand,
  type ThreeWebGpuComputePreparationCache,
} from './threeWebGpuComputeBridge';
import type { PairEndpointEncoding } from './pairAbi';

const WORKGROUP_SIZE = 256;
const CANDIDATE_WORKGROUP_SIZE = 64;
const SPARSE_SLEEPING_MIN_BODY_COUNT = 4096;
// Kept false until the GPU differential regression certifies the complete
// candidate/contact closure. Tests opt in explicitly through the regression
// hook; the final production flip is a one-line audited change.
export const SLEEPING_SPARSE_BROADPHASE_PRODUCTION_ENABLED = false;
const SPARSE_ARGS_RETAIN_SCAN_OFFSET = 0;
const SPARSE_ARGS_RETAIN_SEED_OFFSET = 3;
const SPARSE_ARGS_AWAKE_TRAVERSAL_OFFSET = 6;
const SPARSE_ARGS_WORDS = 9;
// Per body: tight/query min, max, center followed by fat/tree min, max, center.
// Keeping both views in one allocation avoids another candidate bind-group
// entry while allowing the tree to stay conservative and the leaf test exact.
const AABB_FLOATS_PER_BODY = 18;
const AABB_VERTICES_PER_BODY = 6;
const FAT_AABB_VERTEX_OFFSET = 3;
const DIRTY_HEADER_WORDS = 8;
const PAIR_STACK_SIZE = 64;
const SPARSE_DEBUG_BASE = 12;
const SPARSE_DIRTY_LEAVES_COUNTER = SPARSE_DEBUG_BASE + 0;
const SPARSE_DIRTY_ANCESTORS_COUNTER = SPARSE_DEBUG_BASE + 1;
const SPARSE_CONTAINMENT_HITS_COUNTER = SPARSE_DEBUG_BASE + 2;
const SPARSE_CONTAINMENT_MISSES_COUNTER = SPARSE_DEBUG_BASE + 3;
const SPARSE_SUCCESS_FRAMES_COUNTER = SPARSE_DEBUG_BASE + 4;
const FULL_FALLBACK_FRAMES_COUNTER = SPARSE_DEBUG_BASE + 5;
const REFIT_FATAL_COUNTER = SPARSE_DEBUG_BASE + 6;
const REFIT_FATAL_CUMULATIVE_COUNTER = SPARSE_DEBUG_BASE + 7;
const QUALITY_RATIO_COUNTER = 20;
const QUALITY_FAULT_COUNTER = 21;
const QUALITY_SAMPLES_COUNTER = 22;
const QUALITY_FAULTS_COUNTER = 23;
const FORCED_FULL_COUNTER = SPARSE_DEBUG_BASE + 12;
const DENSE_FULL_COUNTER = SPARSE_DEBUG_BASE + 13;
const SPARSE_INPUT_FAULT_COUNTER = SPARSE_DEBUG_BASE + 14;
const POST_FULL_VALIDATION_FAULT_COUNTER = SPARSE_DEBUG_BASE + 15;
const SLEEPING_AABB_SKIPPED_COUNTER = SPARSE_DEBUG_BASE + 17;
// Keep the common LBVH path in a compact private stack. An adversarial Morton
// tie chain that exceeds it aborts candidate publication; bounded worlds may
// recover exactly on the next dispatch, while large worlds rebuild fail-closed.
// 0 capacity loss (scene cumulative)
// 1/2/4/5/6 per-dispatch telemetry
// 3/7 current-topology traversal faults
// 8 per-dispatch capacity abort
// 9/10 traversal faults (scene cumulative)
// 11 ignored-pair hash conservative fallbacks (scene cumulative)
// 28 candidate-dispatch fatal bit (scene cumulative, reset only with scene)
const FATAL_CUMULATIVE_COUNTER = 28;
const DEBUG_COUNTER_WORDS = 30;
// Counter 8 remains the cooperative abort bit for the current dispatch. Every
// path that sets it also sets counter 28, which is never cleared by
// clearCounter(). Sampling the cumulative word means staging-ring
// backpressure can skip a copy without losing the fatal observation: the next
// available dispatch will still copy 1.
// Sample the shared scene-sticky constraint gate and the current topology's
// refit/validation latch. They are copied from separate buffers into this
// compact two-word staging buffer by the candidate encoder.
const FATAL_SAMPLE_BYTES = 8;
// Same-substep GPU gating and transactional rollback already prevent an
// incomplete candidate/contact graph from committing physical state.  The CPU
// map exists only to surface the sticky failure and stop repeating rolled-back
// work; sampling it every frame adds an unnecessary browser/driver sync point
// to the 50K/64K hot path.
const FATAL_SAMPLE_CADENCE_FRAMES = 30;
// Counter 28 is sticky, so more than one pending sample for the same scene
// generation is redundant and can force Chrome to serialize otherwise
// batchable queue work. Four slots only provide reset-generation rotation;
// reserveFatalSampleSlot keeps at most one current-generation map in flight.
const FATAL_SAMPLE_RING_SIZE = 4;
const QUALITY_RATIO_SCALE = 1_000_000;
const QUALITY_REBUILD_THRESHOLD = 1.35;
const MAX_EXACT_DELTA_BODIES = 1024;
// A topology/refit fault may use the exact all-pairs recovery only while its
// work is provably bounded. Production 50K/64K scenes must publish an empty
// candidate stream and rebuild instead of turning one bad LBVH into O(N^2).
export const BROADPHASE_FATAL_EXACT_FALLBACK_BODY_LIMIT = 1024;

export type BroadphaseCandidateEndpointLayout = Readonly<{
  encoding: PairEndpointEncoding;
  candidateCapacity: number;
  packedPairOffset: number | null;
  bodyAOffset: number | null;
  bodyBOffset: number | null;
  requiredWords: number;
}>;

/**
 * Candidate word layout consumed by the native broadphase shader. The packed
 * result is the established `[count, packedPair...]` ABI. Wide mode keeps that
 * header and publishes exact endpoints into two contiguous SoA planes.
 */
export function resolveBroadphaseCandidateEndpointLayout(
  candidateCapacityValue: number,
  encoding: PairEndpointEncoding = 'packed-u16',
): BroadphaseCandidateEndpointLayout {
  if (!Number.isSafeInteger(candidateCapacityValue) || candidateCapacityValue < 0) {
    throw new Error('Broadphase candidate capacity must be a non-negative safe integer.');
  }
  if (encoding !== 'packed-u16' && encoding !== 'wide-u32') {
    throw new Error(`Unknown broadphase pair endpoint encoding: ${String(encoding)}.`);
  }
  const candidateCapacity = candidateCapacityValue;
  if (encoding === 'packed-u16') {
    const requiredWords = 1 + candidateCapacity;
    if (!Number.isSafeInteger(requiredWords)) {
      throw new Error('Packed broadphase candidate layout exceeds the exact integer range.');
    }
    return Object.freeze({
      encoding,
      candidateCapacity,
      packedPairOffset: 1,
      bodyAOffset: null,
      bodyBOffset: null,
      requiredWords,
    });
  }
  const bodyBOffset = 1 + candidateCapacity;
  const requiredWords = bodyBOffset + candidateCapacity;
  if (!Number.isSafeInteger(requiredWords)) {
    throw new Error('Wide broadphase candidate layout exceeds the exact integer range.');
  }
  return Object.freeze({
    encoding,
    candidateCapacity,
    packedPairOffset: null,
    bodyAOffset: 1,
    bodyBOffset,
    requiredWords,
  });
}

export function createBroadphaseCandidatePublicationWgsl(
  layout: BroadphaseCandidateEndpointLayout,
): string {
  if (layout.encoding === 'wide-u32') {
    return `  let writeIndex = atomicAdd(&candidateState[0u], 1u);
  if (writeIndex < uniforms.pairCapacity) {
    pairActivity[${layout.bodyAOffset}u + writeIndex] = a;
    pairActivity[${layout.bodyBOffset}u + writeIndex] = b;
    if (uniforms.debugEnabled > 0u) {
      atomicAdd(&debugCounters[2u], 1u);
    }
  } else {
    atomicAdd(&debugCounters[0u], 1u);
    atomicStore(&debugCounters[8u], 1u);
    atomicStore(&debugCounters[${FATAL_CUMULATIVE_COUNTER}u], 1u);
  }`;
  }
  // Keep the default shader source byte-for-byte identical at the publication
  // site so existing packed worlds retain the same stores and dispatches.
  return `  let packedPair = (a & 0xFFFFu) | ((b & 0xFFFFu) << 16u);
  let writeIndex = atomicAdd(&candidateState[0u], 1u);
  if (writeIndex < uniforms.pairCapacity) {
    pairActivity[writeIndex + 1u] = packedPair;
    if (uniforms.debugEnabled > 0u) {
      atomicAdd(&debugCounters[2u], 1u);
    }
  } else {
    atomicAdd(&debugCounters[0u], 1u);
    atomicStore(&debugCounters[8u], 1u);
    atomicStore(&debugCounters[${FATAL_CUMULATIVE_COUNTER}u], 1u);
  }`;
}

/**
 * The ignored-pair table still stores packed-u16 keys.  Wide endpoints that
 * cannot be represented by that table must stay collidable: suppressing them
 * through a truncated alias would lose a real contact.  Debug runs count the
 * conservative bypass so the missing wide ignored-pair ABI is observable.
 */
export function createBroadphaseWideIgnoredPairGuardWgsl(): string {
  return `  // The legacy ignored-pair table owns packed-u16 keys. Until that
  // independent ABI is widened, never let a truncated key suppress a real
  // wide candidate. Publishing the extra candidate is conservative.
  if (i > 0xffffu || j > 0xffffu) {
    if (uniforms.debugEnabled > 0u) {
      atomicAdd(&debugCounters[11u], 1u);
    }
    return false;
  }
`;
}

/**
 * BroadPhaseStage writes a small set of Three-owned storage attributes through
 * the native WebGPU backend. Three's renderer-side Attributes cache tracks its
 * own uploaded version; updating only the backend cache leaves that version
 * stale and a later renderer.compute() can upload the CPU copy over native GPU
 * results in the same substep. Acknowledging both caches makes native and TSL
 * ownership transitions explicit.
 */
function acknowledgeRendererAttributeVersion(
  renderer: any,
  attribute: StorageBufferAttribute,
): boolean {
  const rendererAttributes = renderer?._attributes;
  if (typeof rendererAttributes?.get !== 'function') return false;
  const attributeState = rendererAttributes.get(attribute);
  if (!attributeState) return false;
  attributeState.version = attribute.version;
  attribute.clearUpdateRanges?.();
  return true;
}

export type BroadphaseFatalRecoveryMode = 'bvh' | 'bounded-exact' | 'reject-rebuild';

export function getBroadphaseFatalRecoveryMode(
  bodyCount: number,
  topologyFatal: boolean,
): BroadphaseFatalRecoveryMode {
  if (!topologyFatal) return 'bvh';
  return Math.max(0, Math.floor(bodyCount)) <= BROADPHASE_FATAL_EXACT_FALLBACK_BODY_LIMIT
    ? 'bounded-exact'
    : 'reject-rebuild';
}

export function runBroadphaseFatalPolicySelfTest(): {
  ok: boolean;
  checks: number;
  failures: string[];
} {
  const failures: string[] = [];
  let checks = 0;
  const expect = (condition: boolean, message: string): void => {
    checks++;
    if (!condition) failures.push(message);
  };
  expect(getBroadphaseFatalRecoveryMode(64_313, false) === 'bvh', 'healthy 64K must use BVH');
  expect(getBroadphaseFatalRecoveryMode(1, true) === 'bounded-exact', 'one-body fatal recovery');
  expect(
    getBroadphaseFatalRecoveryMode(BROADPHASE_FATAL_EXACT_FALLBACK_BODY_LIMIT, true)
      === 'bounded-exact',
    'fatal exact boundary',
  );
  expect(
    getBroadphaseFatalRecoveryMode(BROADPHASE_FATAL_EXACT_FALLBACK_BODY_LIMIT + 1, true)
      === 'reject-rebuild',
    'fatal reject boundary',
  );
  expect(getBroadphaseFatalRecoveryMode(50_225, true) === 'reject-rebuild', '50K fatal reject');
  expect(getBroadphaseFatalRecoveryMode(64_313, true) === 'reject-rebuild', '64K fatal reject');
  // This mirrors finalizeCounter's publication contract: every abort publishes
  // zero even when workgroups wrote a non-empty prefix before observing it.
  const publishCount = (raw: number, capacity: number, aborted: boolean): number => (
    aborted ? 0 : Math.min(Math.max(0, raw), Math.max(0, capacity))
  );
  expect(publishCount(73, 64, true) === 0, 'capacity abort must publish zero');
  expect(publishCount(73, 64, false) === 64, 'healthy publication clamps capacity');
  const packedCandidates = resolveBroadphaseCandidateEndpointLayout(3);
  expect(
    packedCandidates.encoding === 'packed-u16'
      && packedCandidates.packedPairOffset === 1
      && packedCandidates.bodyAOffset === null
      && packedCandidates.bodyBOffset === null
      && packedCandidates.requiredWords === 4,
    'packed candidate layout must preserve the legacy header and pair words',
  );
  const packedPublication = createBroadphaseCandidatePublicationWgsl(packedCandidates);
  expect(
    packedPublication.includes('pairActivity[writeIndex + 1u] = packedPair;')
      && packedPublication.includes('(a & 0xFFFFu) | ((b & 0xFFFFu) << 16u)'),
    'packed candidate shader publication must remain byte-compatible',
  );
  const wideCandidates = resolveBroadphaseCandidateEndpointLayout(8_160_000, 'wide-u32');
  expect(
    wideCandidates.packedPairOffset === null
      && wideCandidates.bodyAOffset === 1
      && wideCandidates.bodyBOffset === 8_160_001
      && wideCandidates.requiredWords === 16_320_001,
    '510K candidate endpoint planes must be contiguous exact u32 SoA',
  );
  const widePublication = createBroadphaseCandidatePublicationWgsl(wideCandidates);
  expect(
    widePublication.includes('pairActivity[1u + writeIndex] = a;')
      && widePublication.includes('pairActivity[8160001u + writeIndex] = b;')
      && !widePublication.includes('0xFFFFu'),
    'wide candidate shader publication must not truncate either endpoint',
  );
  const wideIgnoredGuard = createBroadphaseWideIgnoredPairGuardWgsl();
  expect(
    wideIgnoredGuard.includes('i > 0xffffu || j > 0xffffu')
      && wideIgnoredGuard.includes('atomicAdd(&debugCounters[11u], 1u)')
      && wideIgnoredGuard.includes('return false;'),
    'wide ignored-pair guard must conservatively retain and diagnose unrepresentable endpoints',
  );
  return { ok: failures.length === 0, checks, failures };
}

export type BroadphaseDebugSnapshot = {
  frame: number;
  bodies: number;
  candidates: number;
  capacity: number;
  capacityDrops: number;
  dedupDrops: number;
  writes: number;
  leafCandidates: number;
  visitBudgetDrops: number;
  stackDrops: number;
  epochVisitBudgetDrops: number;
  epochStackDrops: number;
  ignoredPairProbeFallbacks: number;
  pairsPerBody: number;
  topologyQualityRatio: number;
  topologyQualitySamples: number;
  topologyQualityFault: boolean;
  topologyQualityFaults: number;
  dirtyLeaves: number;
  dirtyAncestors: number;
  containmentHits: number;
  containmentMisses: number;
  sparseRefitFrames: number;
  fullRefitFrames: number;
  refitFatal: boolean;
  refitFatalFrames: number;
  forcedFullRefits: number;
  denseFullRefits: number;
  sparseInputFaults: number;
  postFullValidationFaults: number;
  /** Scene-cumulative candidate/refit/traversal fatal word. */
  fatalCumulative: boolean;
  /** Bodies whose certified frozen tight/fat AABBs were retained this frame. */
  sleepingAabbSkippedBodies: number;
};

export type BroadphaseBuildReason =
  | 'initial'
  | 'body-grow'
  | 'body-shrink'
  | 'quality'
  | 'refit-fatal'
  | 'manual';

/**
 * CPU-only LBVH lifecycle telemetry. Counters are cumulative for the lifetime
 * of the stage, while active topology and quality fields describe the current
 * scene generation. Reading this snapshot never submits GPU work.
 */
export type BroadphaseLifecycleSnapshot = {
  buildStarts: number;
  buildPublishes: number;
  buildDiscards: number;
  lastBuildReason: BroadphaseBuildReason | null;
  qualityRequests: number;
  qualityBuildStarts: number;
  activeTopologyEpoch: number;
  lastQualityRatio: number;
  lastQualitySampleFrame: number;
  buildInFlight: boolean;
  activeBodyCount: number;
  sleepingAabbReuseSelectedLast: boolean;
  sleepingAabbReuseSelections: number;
};

/** CPU-only allocation/use proof for the optional dense pair-visited bitset. */
export type BroadphasePairDedupTelemetry = Readonly<{
  configured: boolean;
  wordCapacity: number;
  bytesPerDomain: number;
  requiredWordsLast: number;
  effectiveLast: boolean;
  clearWorkgroupsLast: number;
  frameLast: number;
}>;

/** CPU-only state for the fused fatal-sample staging ring. */
export type BroadphaseFatalSampleDiagnostics = Readonly<{
  /** Samples belonging to the current reset generation. */
  pendingSamples: number;
  /** All occupied ring slots, including stale generations awaiting mapAsync. */
  totalPendingSamples: number;
  backpressureSkips: number;
  infrastructureFailure: boolean;
  /** Latest scene-generation dispatch whose cumulative fatal word was mapped. */
  lastCompletedSampleFrame: number;
}>;

type BroadphaseFatalSampleSlot = {
  buffer: GPUBuffer;
  inFlight: boolean;
  generation: number;
  topologyEpoch: number;
  frameId: number;
  destroyWhenComplete: boolean;
};

/**
 * Optional caller-owned compute tail appended after candidate generation.
 * Status is deliberately mutable so every early/fallback exit is observable
 * without changing the established broadphase dispatch control flow.
 */
export type BroadphaseComputeTail = {
  commands: readonly ThreeWebGpuComputeCommand[];
  combinedSubmissionLabel: string;
  substepIndex: number;
  fused: boolean;
  broadphaseSubmitted: boolean;
  fallbackReason: string | null;
};

type BroadphaseCandidatePipelineSet = Readonly<{
  clearCounter: GPUComputePipeline;
  clearVisited: GPUComputePipeline;
  seedRetainedPairs: GPUComputePipeline;
  emitPairs: GPUComputePipeline;
  denseEmitPairs: GPUComputePipeline;
  finalizeCounter: GPUComputePipeline;
  denseFinalizeCounter: GPUComputePipeline;
  captureRetainedPrefix: GPUComputePipeline;
  publishCandidateSafety: GPUComputePipeline;
}>;

type BvhBuildBackend = {
  buildAsyncFromGPUBuffers: (options: {
    positionBuffer: GPUBuffer;
    indexBuffer: GPUBuffer;
    primCount: number;
    positionStride?: number;
    useFlatten?: boolean;
    waitForGpuCompletion?: boolean;
    submissionFrameId?: number;
  }) => Promise<void>;
  refitAsyncFromGPUBuffers?: (options: {
    positionBuffer: GPUBuffer;
    indexBuffer: GPUBuffer;
    primCount: number;
    positionStride?: number;
    waitForGpuCompletion?: boolean;
    sampleQuality?: boolean;
    submissionFrameId?: number;
  }) => Promise<void>;
  recordRefitFromGPUBuffers?: (
    commandEncoder: GPUCommandEncoder,
    options: {
      positionBuffer: GPUBuffer;
      indexBuffer: GPUBuffer;
      primCount: number;
      positionStride?: number;
      sampleQuality?: boolean;
      dirtyLeafBuffer: GPUBuffer;
      dirtyLeafCapacity: number;
      debugBuffer: GPUBuffer;
      debugCounterOffset: number;
      forceFullRefit?: boolean;
      resetQualityBaseline?: boolean;
    },
  ) => boolean;
  recordFullRefitFromGPUBuffers?: (
    commandEncoder: GPUCommandEncoder,
    options: {
      positionBuffer: GPUBuffer;
      indexBuffer: GPUBuffer;
      primCount: number;
      positionStride?: number;
      sampleQuality?: boolean;
    },
  ) => boolean;
  prewarm?: (primCapacity: number) => Promise<void>;
  readonly bvh2Buffer: GPUBuffer | null;
  readonly clusterIdxBuffer: GPUBuffer | null;
  readonly parentIdxBuffer: GPUBuffer | null;
  readonly refitTreeDepth?: number | null;
  readonly qualityMetricsBuffer?: GPUBuffer | null;
  dispose: () => void;
};

type BroadphaseDebugReadbackContext = Readonly<{
  frameId: number;
  bodyCount: number;
  generation: number;
  topologyEpoch: number;
  sequence: number;
  buffer: GPUBuffer;
}>;

export type BroadphaseSleepingSparseInputs = Readonly<{
  bodyState: StorageBufferAttribute;
  persistentManifoldMap: StorageBufferAttribute;
  compactLayout: CompactContactPoolLayout;
  maxPersistentManifolds: number;
  pairManifoldSlots: number;
  retainedPairScratchOffset: number;
  awakeBodyScratchOffset: number;
  awakeBodyScratchCapacity: number;
  getCertificate: () => SleepingSparseBroadphaseCertificate;
}>;

export type BroadphaseSleepingSparseDiagnostics = Readonly<{
  attached: boolean;
  enabled: boolean;
  minimumBodyCount: number;
  selectedLast: boolean;
  selectedDispatches: number;
  certificateGenerationLast: number;
  forcedFullForTesting: boolean;
}>;

export type BroadphaseSleepingSparseScratchDiagnostics = Readonly<{
  retainedCount: number;
  awakeCount: number;
  validatorFaultBits: number;
  retainedPrefixScratch: number;
  bodyStatePrefix: readonly number[];
}>;

export type BroadphaseStartupPipelineState = 'idle' | 'pending' | 'ready' | 'error';

/** CPU-only proof that every raw broadphase pipeline is safe to record. */
export type BroadphaseStartupPrewarmDiagnostics = Readonly<{
  state: Exclude<BroadphaseStartupPipelineState, 'idle'>;
  candidatePipelines: BroadphaseStartupPipelineState;
  lbvhPipelines: BroadphaseStartupPipelineState;
  sparsePipelines: BroadphaseStartupPipelineState;
  error: string | null;
}>;

export class BroadPhaseStage {
  private readonly gpuBVHs: [BvhBuildBackend, BvhBuildBackend];
  private readonly enableBvhBuild: boolean;
  private readonly buildOnce: boolean;
  private readonly rebuildIntervalFrames: number;
  private readonly waitForGpuCompletion: boolean;
  private readonly device: GPUDevice;
  private readonly submissionLedger?: PhysicsSubmissionLedger;
  private readonly onConstraintSafetyBits?: (bits: number) => void;
  private nativeComputePassDescriptorFactory?: NativeComputePassDescriptorFactory;
  private readonly updateAabbPreparationCache: ThreeWebGpuComputePreparationCache =
    createThreeWebGpuComputePreparationCache();
  private readonly computeTailPreparationCache: ThreeWebGpuComputePreparationCache =
    createThreeWebGpuComputePreparationCache();
  private readonly maxBodies: number;
  private readonly maxPairs: number;
  private readonly maxPairsPerBody: number;
  private readonly ignoredPairHashCapacity: number;
  private readonly pairVisitedDedupConfigured: boolean;
  private readonly pairEndpointEncoding: PairEndpointEncoding;
  private readonly candidateEndpointLayout: BroadphaseCandidateEndpointLayout;
  private readonly updateAabbKernel: any;
  private readonly aabbSleepingBodyStateAttr: StorageBufferAttribute;
  private readonly bootstrapKernel: any;
  private readonly positionsAttr: StorageBufferAttribute;
  private readonly shapesAttr: StorageBufferAttribute;
  private readonly pairActivityAttr: StorageBufferAttribute;
  private readonly aabbPositionAttr: StorageBufferAttribute;
  private readonly dirtyLeafAttr: StorageBufferAttribute;
  private readonly aabbSnapshotBuffer: GPUBuffer;
  private readonly aabbIndexBuffer: GPUBuffer;
  private readonly pairCandidateIndicesAttr: StorageBufferAttribute;
  private readonly pairVisitedBitsAttr: StorageBufferAttribute;
  private readonly ignoredPairHashAttr: StorageBufferAttribute;
  private readonly safetyGateAttr: StorageBufferAttribute;
  private readonly candidateUniformBuffer: GPUBuffer;
  /** Dedicated cache line for the high-contention candidate append counter. */
  private readonly candidateCounterBuffer: GPUBuffer;
  private readonly debugCountersBuffer: GPUBuffer;
  private debugReadbackBuffer: GPUBuffer;
  private readonly fatalSampleSlots: BroadphaseFatalSampleSlot[] = [];
  private readonly candidateBindGroupLayout: GPUBindGroupLayout;
  private candidatePipelines: BroadphaseCandidatePipelineSet | null = null;
  private readonly publishCandidateSafetyBindGroupLayout: GPUBindGroupLayout;
  private publishCandidateSafetyBindGroup: GPUBindGroup | null = null;
  private publishCandidateSafetyGateBuffer: GPUBuffer | null = null;
  private candidateBindGroup: GPUBindGroup | null = null;
  private candidateBindGroupBuffers: {
    positions: GPUBuffer | null;
    aabbPosition: GPUBuffer | null;
    bvh: GPUBuffer | null;
    parentIdx: GPUBuffer | null;
    pairCandidate: GPUBuffer | null;
    pairVisited: GPUBuffer | null;
    pairActivity: GPUBuffer | null;
    shapes: GPUBuffer | null;
    ignoredPairHash: GPUBuffer | null;
    candidateCounter: GPUBuffer | null;
  } = {
    positions: null,
    aabbPosition: null,
    bvh: null,
    parentIdx: null,
    pairCandidate: null,
    pairVisited: null,
    pairActivity: null,
    shapes: null,
    ignoredPairHash: null,
    candidateCounter: null,
  };
  private buildInFlight: Promise<void> | null = null;
  private prewarmInFlight: Promise<void> | null = null;
  private candidatePipelinePrewarm: Promise<void> = Promise.resolve();
  private lbvhPipelinePrewarm: Promise<void> = Promise.resolve();
  private sparsePipelinePrewarm: Promise<void> | null = null;
  private candidatePipelineState: BroadphaseStartupPipelineState = 'idle';
  private lbvhPipelineState: BroadphaseStartupPipelineState = 'idle';
  private sparsePipelineState: BroadphaseStartupPipelineState = 'idle';
  private candidatePipelineError: Error | null = null;
  private lbvhPipelineError: Error | null = null;
  private sparsePipelineError: Error | null = null;
  private buildInFlightStartFrame = -1;
  private buildInFlightBodyCount = -1;
  private activeBvhIndex = 0;
  private activeBvhBodyCount = -1;
  private activeBvhSnapshotFrame = -1;
  private activeBvhNeedsFullRefit = false;
  private latestAabbPositionBuffer: GPUBuffer | null = null;
  private requestedBodyCount = -1;
  private lastBuildMs = 0;
  private candidatePairsEnabled = false;
  private backendBuffersReady = false;
  private bvhBuffersReady = false;
  private storageBuffersInitialized = false;
  private readonly dirtyHeader = new Uint32Array(DIRTY_HEADER_WORDS);
  private storageInitWarned = false;
  private bootstrapDone = false;
  private lastBuildFrame = -1;
  private hasBuiltOnce = false;
  private buildGeneration = 0;
  private buildStarts = 0;
  private buildPublishes = 0;
  private buildDiscards = 0;
  private lastBuildReason: BroadphaseBuildReason | null = null;
  private qualityRequests = 0;
  private qualityBuildStarts = 0;
  private pendingRebuildReason: 'quality' | 'refit-fatal' | null = null;
  private pendingMaintenanceBuildReason: BroadphaseBuildReason | null = null;
  private refitFailureWarned = false;
  private ignoredPairHashUploadedVersion = -1;
  private safetyGateUploadedVersion = -1;
  private debugEnabled = false;
  private debugReadbackInFlight = false;
  private debugEveryNFrames = 30;
  private lastDebugLogFrame = -1;
  private candidateOverflowDetected = false;
  private fatalSampleInfrastructureFailure = false;
  private fatalSampleFailureWarned = false;
  private fatalSampleBackpressureSkips = 0;
  private lastCompletedFatalSampleFrame = -1;
  private lastScheduledFatalSampleFrame = -1;
  private readonly safetyReadbackTimeoutMs: number;
  private forceFatalSampleReadbackTimeoutForTesting = false;
  private lastDebugSnapshot: BroadphaseDebugSnapshot | null = null;
  private topologyQualityRebuildRequested = false;
  private activeTopologyEpoch = 0;
  private debugReadbackSequence = 0;
  private lastAppliedDebugReadbackSequence = -1;
  private lastDispatchFrame = -1;
  // This cooldown anchor is deliberately separate from the last completed
  // quality sample exposed by getLifecycleSnapshot(). Publication starts the
  // cadence without pretending that a sample has already completed.
  private qualitySampleCooldownFrame = -1;
  private lastQualitySampleFrame = -1;
  private lastQualityRatio = 1.0;
  private lastObservedQualitySamples = 0;
  private directFullRefitUntilFrame = -1;
  private fatInitializedBodyCount = 0;
  private updateAabbNativeRenderer: any = null;
  private updateAabbNativeReady = false;
  private updateAabbNativeFallbacks = 0;
  private currentFixedStepId: number | null = null;
  private currentFixedStepSpan = 1;
  private forceRefitFatalForTesting = false;
  private pairVisitedRequiredWordsLast = 0;
  private pairVisitedDedupEffectiveLast = false;
  private pairVisitedClearWorkgroupsLast = 0;
  private pairVisitedFrameLast = -1;
  private sparseSleepingInputs: BroadphaseSleepingSparseInputs | null = null;
  private sleepingAabbCertificateProvider: (() => SleepingSparseBroadphaseCertificate) | null = null;
  private sleepingAabbReuseSelectedLast = false;
  private sleepingAabbReuseSelections = 0;
  private sleepingAabbCertificateFailureWarned = false;
  private sparseSleepingUniformBuffer: GPUBuffer | null = null;
  private sparseSleepingArgsBuffer: GPUBuffer | null = null;
  private sparseSleepingBindGroupLayout: GPUBindGroupLayout | null = null;
  private sparseSleepingWorkBindGroupLayout: GPUBindGroupLayout | null = null;
  private sparseSleepingBindGroup: GPUBindGroup | null = null;
  private sparseSleepingWorkBindGroup: GPUBindGroup | null = null;
  private sparseSleepingBindGroupBuffers: readonly GPUBuffer[] | null = null;
  private sparseClearPipeline: GPUComputePipeline | null = null;
  private sparseRetainPipeline: GPUComputePipeline | null = null;
  private sparseCompactAwakePipeline: GPUComputePipeline | null = null;
  private sparseFinalizePipeline: GPUComputePipeline | null = null;
  private sparseSelectedLast = false;
  private sparseSelectedDispatches = 0;
  private sparseCertificateGenerationLast = -1;
  private forceFullSparseSleepingForTesting = false;
  private sparseSleepingEnabled = SLEEPING_SPARSE_BROADPHASE_PRODUCTION_ENABLED;
  private sparseSleepingMinimumBodyCount = SPARSE_SLEEPING_MIN_BODY_COUNT;
  private disposed = false;

  private getActiveBVH(): BvhBuildBackend {
    return this.gpuBVHs[this.activeBvhIndex];
  }

  private getBuildBVH(): BvhBuildBackend {
    return this.gpuBVHs[1 - this.activeBvhIndex];
  }

  private makeStartupPipelineError(label: string, error: unknown): Error {
    const detail = error instanceof Error ? error.message : String(error);
    return new Error(`${label}: ${detail}`);
  }

  private getStartupPipelineError(): Error | null {
    return this.candidatePipelineError
      ?? this.lbvhPipelineError
      ?? this.sparsePipelineError;
  }

  private isStartupPipelineReady(): boolean {
    return this.candidatePipelineState === 'ready'
      && this.lbvhPipelineState === 'ready'
      && (this.sparsePipelineState === 'idle' || this.sparsePipelineState === 'ready');
  }

  private getCandidatePipelineSet() {
    return this.candidatePipelines;
  }

  private getStartupPipelineWaitReason(): string {
    if (this.candidatePipelineState !== 'ready') return 'candidatePipelines';
    if (this.lbvhPipelineState !== 'ready') return 'lbvhPipelines';
    if (this.sparsePipelineState !== 'idle' && this.sparsePipelineState !== 'ready') {
      return 'sparsePipelines';
    }
    return 'unknownPipelines';
  }

  /**
   * Extends the existing dispatch/build fence without allowing an older
   * completion callback to clear a newer sparse-pipeline compilation.
   */
  private appendStartupPrewarmFence(promise: Promise<void>): void {
    const prior = this.prewarmInFlight;
    const fence = Promise.all([
      prior ?? Promise.resolve(),
      promise,
    ]).then(() => undefined);
    this.prewarmInFlight = fence;
    void fence.finally(() => {
      if (this.prewarmInFlight === fence) this.prewarmInFlight = null;
    });
  }

  private requestTopologyRebuild(reason: 'quality' | 'refit-fatal'): void {
    if (reason === 'quality' && !this.topologyQualityRebuildRequested) {
      this.qualityRequests++;
    }
    this.topologyQualityRebuildRequested = true;
    // A refit-fatal request is the stronger cause and must not be hidden by an
    // earlier quality request while a build is waiting for its cooldown/fence.
    if (this.pendingRebuildReason !== 'refit-fatal' || reason === 'refit-fatal') {
      this.pendingRebuildReason = reason;
    }
  }

  private createDebugReadbackBuffer(): GPUBuffer {
    return this.device.createBuffer({
      label: 'Broadphase Debug Readback',
      size: DEBUG_COUNTER_WORDS * 4,
      usage: GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ,
    });
  }

  private createFatalSampleSlot(): BroadphaseFatalSampleSlot {
    return {
      buffer: this.device.createBuffer({
        label: 'Broadphase Fatal Sample Readback',
        size: FATAL_SAMPLE_BYTES,
        usage: GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ,
      }),
      inFlight: false,
      generation: -1,
      topologyEpoch: -1,
      frameId: -1,
      destroyWhenComplete: false,
    };
  }

  private reserveFatalSampleSlot(frameId: number): BroadphaseFatalSampleSlot | null {
    if (this.fatalSampleSlots.some((candidate) => (
      candidate.inFlight && candidate.generation === this.buildGeneration
    ))) {
      // A pending sample already covers the cumulative fatal state through
      // its copy point. Keep later work batchable; once it resolves, the next
      // dispatch samples the still-sticky GPU word again.
      this.fatalSampleBackpressureSkips++;
      return null;
    }
    const slot = this.fatalSampleSlots.find((candidate) => (
      !candidate.inFlight && !candidate.destroyWhenComplete
    ));
    if (!slot) {
      // Every occupied slot belongs to an older reset generation, otherwise
      // the early return above would have found a current sample. With no
      // current-generation map there is no watchdog covering this stream and
      // stale callbacks intentionally ignore it. Bounded-ring exhaustion is
      // therefore a correctness failure, not ordinary backpressure.
      this.fatalSampleBackpressureSkips++;
      this.latchFatalSampleInfrastructureFailure(
        `Broadphase fatal-sample ring exhausted at frame=${frameId}; physics must fail closed.`,
        new Error('all fatal-sample slots are pending from stale reset generations'),
      );
      return null;
    }
    slot.inFlight = true;
    slot.generation = this.buildGeneration;
    slot.topologyEpoch = this.activeTopologyEpoch;
    slot.frameId = frameId;
    return slot;
  }

  private releaseFatalSampleSlot(slot: BroadphaseFatalSampleSlot): void {
    slot.inFlight = false;
    slot.generation = -1;
    slot.topologyEpoch = -1;
    slot.frameId = -1;
    if (slot.destroyWhenComplete) {
      slot.buffer.destroy();
    }
  }

  private latchFatalSampleInfrastructureFailure(message: string, error: unknown): void {
    this.fatalSampleInfrastructureFailure = true;
    if (!this.fatalSampleFailureWarned) {
      console.error(message, error);
      this.fatalSampleFailureWarned = true;
    }
  }

  private beginFatalSampleReadback(slot: BroadphaseFatalSampleSlot): void {
    const sampleGeneration = slot.generation;
    const sampleTopologyEpoch = slot.topologyEpoch;
    const sampleFrameId = slot.frameId;
    let mapPromise: Promise<void>;
    try {
      mapPromise = slot.buffer.mapAsync(GPUMapMode.READ);
    } catch (error) {
      if (sampleGeneration === this.buildGeneration && !this.disposed) {
        this.latchFatalSampleInfrastructureFailure(
          `Broadphase fatal sample map failed synchronously at frame=${sampleFrameId}; physics must fail closed.`,
          error,
        );
      }
      this.releaseFatalSampleSlot(slot);
      return;
    }
    if (this.forceFatalSampleReadbackTimeoutForTesting) {
      this.forceFatalSampleReadbackTimeoutForTesting = false;
      const mapped = mapPromise;
      mapPromise = mapped.then(() => new Promise<void>((resolve) => {
        setTimeout(resolve, this.safetyReadbackTimeoutMs * 3);
      }));
    }
    const timeoutError = new Error(
      `Broadphase fatal sample readback timed out after ${this.safetyReadbackTimeoutMs}ms.`,
    );
    const timeoutHandle = setTimeout(() => {
      if (sampleGeneration === this.buildGeneration && !this.disposed) {
        this.latchFatalSampleInfrastructureFailure(
          `Broadphase fatal sample readback timed out at frame=${sampleFrameId}; physics must fail closed.`,
          timeoutError,
        );
      }
      // mapAsync cannot be cancelled. Keep this slot in-flight until its own
      // promise settles; reusing or unmapping a pending GPUBuffer is invalid.
    }, this.safetyReadbackTimeoutMs);
    void mapPromise.then(() => {
      try {
        const values = new Uint32Array(slot.buffer.getMappedRange());
        const safetyBits = values[0] ?? 0;
        const refitFatal = (values[1] ?? 0) !== 0;
        if (sampleGeneration === this.buildGeneration) {
          this.lastCompletedFatalSampleFrame = Math.max(
            this.lastCompletedFatalSampleFrame,
            sampleFrameId,
          );
          if ((safetyBits & CONSTRAINT_SAFETY_BROADPHASE_FATAL_BIT) !== 0) {
            this.candidateOverflowDetected = true;
          }
          const contactSafetyBits = safetyBits & (
            CONSTRAINT_SAFETY_CONTACT_FATAL_BIT | CONSTRAINT_SAFETY_CCD_FATAL_BIT
          );
          if (contactSafetyBits !== 0) {
            this.onConstraintSafetyBits?.(contactSafetyBits);
          }
          if (refitFatal && sampleTopologyEpoch === this.activeTopologyEpoch) {
            // A large-scene fatal dispatch has already published a zero-length
            // candidate stream. Make the CPU-facing topology unavailable and
            // request a latest-wins rebuild; the next dispatch snapshots live
            // AABBs without traversing the invalid tree as an O(N^2) fallback.
            this.candidatePairsEnabled = false;
            this.bvhBuffersReady = false;
            this.activeBvhNeedsFullRefit = true;
            this.activeBvhSnapshotFrame = -1;
            this.lastBuildFrame = -1;
            this.requestTopologyRebuild('refit-fatal');
            // Promise callbacks must never submit queue work outside the next
            // animation frame's observer/fence scope. PhysicsEngine services
            // this pending maintenance request synchronously before applying
            // the sticky halt on its next tick.
          }
        }
      } finally {
        // dispose() may have destroyed an already-completed mapping between
        // promise resolution and this callback. Keep teardown fail-safe.
        try {
          slot.buffer.unmap();
        } catch {
          // The stage is terminal or the device has already been lost.
        }
      }
    }).catch((error) => {
      if (sampleGeneration === this.buildGeneration && !this.disposed) {
        // A failed safety readback is indistinguishable from an incomplete
        // candidate stream. Latch it separately so telemetry does not claim a
        // GPU capacity overflow that was never observed.
        this.latchFatalSampleInfrastructureFailure(
          `Broadphase fatal sample readback failed at frame=${sampleFrameId}; physics must fail closed.`,
          error,
        );
      }
    }).finally(() => {
      clearTimeout(timeoutHandle);
      this.releaseFatalSampleSlot(slot);
    });
  }

  private startBuild(
    aabbPositionBuffer: GPUBuffer,
    bodyCount: number,
    frameId: number,
    reason: BroadphaseBuildReason,
  ): void {
    if (this.disposed || this.buildInFlight) {
      return;
    }
    this.pendingMaintenanceBuildReason = null;

    const buildStart = performance.now();
    this.lastBuildFrame = frameId;
    this.buildInFlightStartFrame = frameId;
    this.buildInFlightBodyCount = bodyCount;
    const buildBVHIndex = 1 - this.activeBvhIndex;
    const buildBVH = this.getBuildBVH();
    const buildGeneration = this.buildGeneration;
    this.buildStarts++;
    this.lastBuildReason = reason;
    if (reason === 'quality') {
      this.qualityBuildStarts++;
    }

    // Snapshot AABBs so traversal always reads from a fully-built BVH generated
    // from immutable input data.
    const copyBytes = Math.max(4, bodyCount * AABB_FLOATS_PER_BODY * 4);
    const snapshotEncoder = this.device.createCommandEncoder({ label: 'Broadphase AABB Snapshot Copy' });
    snapshotEncoder.copyBufferToBuffer(aabbPositionBuffer, 0, this.aabbSnapshotBuffer, 0, copyBytes);
    const submitSnapshot = (): void => this.device.queue.submit([snapshotEncoder.finish()]);
    if (this.submissionLedger) {
      this.submissionLedger.withActualSubmitScope(
        this.device.queue,
        {
          frameId,
          submissionClass: 'topology',
          label: 'broadphase-aabb-snapshot',
        },
        submitSnapshot,
      );
    } else {
      submitSnapshot();
    }
    this.submissionLedger?.record('topology', 'broadphase-aabb-snapshot', { frameId });

    const buildPromise = buildBVH.buildAsyncFromGPUBuffers({
      positionBuffer: this.aabbSnapshotBuffer,
      indexBuffer: this.aabbIndexBuffer,
      primCount: bodyCount,
      positionStride: 3,
      useFlatten: false,
      waitForGpuCompletion: this.waitForGpuCompletion,
      submissionFrameId: frameId,
    }).then(() => {
      // Runtime creation is append-only within one broadphase generation.
      // Publish the newest completed prefix even when more bodies arrived
      // while it was building: traversal uses activeBvhBodyCount for this
      // exact prefix and covers the appended tail separately. Reject only a
      // topology that belongs to another generation, is newer than the
      // currently requested prefix (a shrink), or would move backwards.
      // Requiring exact equality here starves a continuously growing scene:
      // every completed build would otherwise be discarded as stale.
      if (
        this.disposed
        || buildGeneration !== this.buildGeneration
        || bodyCount > this.requestedBodyCount
        || bodyCount < this.activeBvhBodyCount
      ) {
        this.buildDiscards++;
        return;
      }
      this.activeBvhIndex = buildBVHIndex;
      this.activeBvhBodyCount = bodyCount;
      const publicationFrame = Math.max(frameId, this.lastDispatchFrame);
      this.activeBvhSnapshotFrame = publicationFrame;
      // The asynchronous build consumed an immutable snapshot.  Before its
      // first traversal, refit every leaf from the current live fat AABBs in
      // the same command buffer as candidate generation.
      this.activeBvhNeedsFullRefit = true;
      this.directFullRefitUntilFrame = -1;
      this.hasBuiltOnce = true;
      // Any traversal diagnostics copied before this publication belong to
      // the replaced topology.  Advancing the epoch prevents those delayed
      // mapAsync callbacks from requesting another rebuild of the new tree.
      this.activeTopologyEpoch++;
      this.topologyQualityRebuildRequested = false;
      this.pendingRebuildReason = null;
      // Cooldowns start at publication, not at asynchronous build launch.
      // Otherwise a long initial build is already "old" when it becomes live
      // and immediately stacks a quality sample/rebuild onto the first step.
      this.lastBuildFrame = publicationFrame;
      this.qualitySampleCooldownFrame = publicationFrame;
      this.lastQualitySampleFrame = -1;
      this.lastQualityRatio = 1.0;
      this.lastObservedQualitySamples = 0;
      // 3/7 are per-topology counters.  The scene-cumulative mirrors at 9/10
      // are intentionally never cleared here, so a rebuild cannot erase a
      // historical loss before the final queue-drained diagnostics read it.
      this.device.queue.writeBuffer(this.debugCountersBuffer, 3 * 4, new Uint32Array([0]));
      this.device.queue.writeBuffer(this.debugCountersBuffer, 7 * 4, new Uint32Array([0]));
      this.device.queue.writeBuffer(
        this.debugCountersBuffer,
        REFIT_FATAL_COUNTER * 4,
        new Uint32Array([0]),
      );
      // The topology's first traversal is also its mandatory live full refit.
      // Bypass the normal telemetry cadence so a fatal validator result is
      // scheduled for readback immediately (or on the first frame after an
      // older in-flight map completes).
      this.lastDebugLogFrame = -1;
      this.refitFailureWarned = false;
      this.candidateBindGroup = null;
      this.candidateBindGroupBuffers.bvh = null;
      this.candidateBindGroupBuffers.parentIdx = null;
      this.lastBuildMs = performance.now() - buildStart;
      this.buildPublishes++;
    }).catch((error) => {
      this.buildDiscards++;
      if (this.disposed || buildGeneration !== this.buildGeneration) {
        return;
      }
      this.lastBuildMs = 0;
      console.warn('BroadPhaseStage BVH build failed:', error);
    }).finally(() => {
      // Keep an invalidated/reset build tracked until its backend work has
      // finished. Clearing it eagerly permits a second build to mutate the
      // same GPULBVHBuilder while the first one is still allocating/submitting.
      if (this.buildInFlight === buildPromise) {
        this.buildInFlight = null;
        this.buildInFlightStartFrame = -1;
        this.buildInFlightBodyCount = -1;
      }
    });
    this.buildInFlight = buildPromise;
  }

  /** Services deferred fatal, quality, and body-growth builds in an observed tick. */
  servicePendingMaintenance(frameId: number): boolean {
    // A validator failure is stronger than an already queued quality/body
    // growth refresh. Preserve that cause in lifecycle telemetry and make the
    // fail-closed recovery unambiguous when both requests arrive together.
    const reason = this.pendingRebuildReason === 'refit-fatal'
      ? 'refit-fatal'
      : this.pendingMaintenanceBuildReason;
    if (
      this.disposed
      || reason === null
      || this.buildInFlight !== null
      || this.prewarmInFlight !== null
      || !this.isStartupPipelineReady()
      || !this.latestAabbPositionBuffer
      || this.requestedBodyCount < 2
    ) {
      return false;
    }
    this.startBuild(
      this.latestAabbPositionBuffer,
      this.requestedBodyCount,
      frameId,
      reason,
    );
    return this.buildInFlight !== null;
  }

  constructor(
    device: GPUDevice,
    positions: StorageBufferAttribute,
    velocities: StorageBufferAttribute,
    angularVelocities: StorageBufferAttribute,
    quaternions: StorageBufferAttribute,
    shapes: StorageBufferAttribute,
    pairActivity: StorageBufferAttribute,
    pairCandidateIndices: StorageBufferAttribute,
    pairVisitedBits: StorageBufferAttribute,
    ignoredPairHash: StorageBufferAttribute,
    maxBodies: number,
    maxPairs: number,
    maxPairsPerBody: number,
    ignoredPairHashCapacity: number,
    options?: {
      enableBvhBuild?: boolean;
      buildOnce?: boolean;
      rebuildIntervalFrames?: number;
      waitForGpuCompletion?: boolean;
      prewarm?: boolean;
      gravity?: [number, number, number];
      submissionLedger?: PhysicsSubmissionLedger;
      enablePairVisitedDedup?: boolean;
      /**
       * Candidate endpoint ABI. Omitted keeps the established packed-u16
       * stream; wide-u32 writes exact bodyA/bodyB SoA planes in pairActivity.
       */
      pairEndpointEncoding?: PairEndpointEncoding;
      safetyReadbackTimeoutMs?: number;
      /** Caller-owned two-word state: fatal gate at 0, retained prefix at 1. */
      safetyGate?: StorageBufferAttribute;
      /** Queue-ordered sleeping state shared with SleepingIslandsStage. */
      sleepingBodyState?: StorageBufferAttribute;
      /** Publishes contact/CCD bits mapped by the fused fatal-sample ring. */
      onConstraintSafetyBits?: (bits: number) => void;
    },
  ) {
    this.device = device;
    this.submissionLedger = options?.submissionLedger;
    this.onConstraintSafetyBits = options?.onConstraintSafetyBits;
    this.maxBodies = maxBodies;
    this.maxPairs = maxPairs;
    this.maxPairsPerBody = maxPairsPerBody;
    this.ignoredPairHashCapacity = ignoredPairHashCapacity;
    this.pairVisitedDedupConfigured = options?.enablePairVisitedDedup ?? true;
    this.pairEndpointEncoding = options?.pairEndpointEncoding ?? 'packed-u16';
    this.candidateEndpointLayout = resolveBroadphaseCandidateEndpointLayout(
      maxPairs,
      this.pairEndpointEncoding,
    );
    this.safetyReadbackTimeoutMs = normalizeConstraintSafetyReadbackTimeoutMs(
      options?.safetyReadbackTimeoutMs,
    );
    this.positionsAttr = positions;
    this.shapesAttr = shapes;
    this.pairActivityAttr = pairActivity;
    if (
      this.pairEndpointEncoding === 'wide-u32'
      && pairActivity.count < this.candidateEndpointLayout.requiredWords
    ) {
      throw new Error(
        `BroadPhaseStage wide candidate planes require at least `
        + `${this.candidateEndpointLayout.requiredWords} pairActivity words `
        + `(received ${pairActivity.count}).`,
      );
    }
    this.enableBvhBuild = options?.enableBvhBuild ?? true;
    this.buildOnce = options?.buildOnce ?? false;
    this.rebuildIntervalFrames = Math.max(1, Math.floor(options?.rebuildIntervalFrames ?? 1));
    this.waitForGpuCompletion = options?.waitForGpuCompletion ?? true;
    this.pairCandidateIndicesAttr = pairCandidateIndices;
    this.pairVisitedBitsAttr = pairVisitedBits;
    this.ignoredPairHashAttr = ignoredPairHash;
    this.safetyGateAttr = options?.safetyGate
      ?? new StorageBufferAttribute(new Uint32Array(2), 1);
    if (this.safetyGateAttr.count < 2) {
      throw new Error(
        'BroadPhaseStage safetyGate requires two u32 words (fatal gate + retained prefix).',
      );
    }
    this.aabbSleepingBodyStateAttr = options?.sleepingBodyState
      ?? new StorageBufferAttribute(new Uint32Array(maxBodies), 1);
    if (this.aabbSleepingBodyStateAttr.count < maxBodies) {
      throw new Error(
        `BroadPhaseStage sleepingBodyState capacity ${this.aabbSleepingBodyStateAttr.count} `
        + `is below maxBodies=${maxBodies}.`,
      );
    }

    this.gpuBVHs = [
      new GPULBVHBuilder(device, {
        // OneSweep's decoupled look-back waits for other workgroups from
        // inside the shader. WebGPU does not guarantee forward progress
        // between workgroups, so a saturated adapter can wait forever. The
        // builtin radix path is multi-pass and has command-buffer barriers
        // between each dependency instead.
        sorterType: LBVHSorterType.BUILTIN,
        submissionLedger: this.submissionLedger,
      }),
      new GPULBVHBuilder(device, {
        sorterType: LBVHSorterType.BUILTIN,
        submissionLedger: this.submissionLedger,
      }),
    ];

    if (options?.prewarm ?? true) {
      this.lbvhPipelineState = 'pending';
      const prewarmCapacity = Math.max(1, this.maxBodies);
      const prewarmPromises: Promise<void>[] = [];
      if (typeof this.gpuBVHs[0].prewarm === 'function') {
        prewarmPromises.push(this.gpuBVHs[0].prewarm(prewarmCapacity));
      }
      if (typeof this.gpuBVHs[1].prewarm === 'function') {
        prewarmPromises.push(this.gpuBVHs[1].prewarm(prewarmCapacity));
      }
      if (prewarmPromises.length > 0) {
        this.lbvhPipelinePrewarm = Promise.allSettled(prewarmPromises).then((results) => {
          // Pipeline creation cannot be cancelled. Once dispose() owns the
          // builders, a late completion must not republish lifecycle state.
          if (this.disposed) return;
          const rejected = results.find(
            (result): result is PromiseRejectedResult => result.status === 'rejected',
          );
          if (rejected) throw rejected.reason;
          this.lbvhPipelineState = 'ready';
        }).catch((error) => {
          if (this.disposed) return;
          this.lbvhPipelineError = this.makeStartupPipelineError(
            'BroadPhaseStage LBVH pipeline prewarm failed',
            error,
          );
          this.lbvhPipelineState = 'error';
          console.error(this.lbvhPipelineError);
        });
      } else {
        this.lbvhPipelineState = 'ready';
      }
    } else {
      this.lbvhPipelineState = 'ready';
    }

    // Six tightly-packed vec3 vertices per body. The first three are the exact
    // swept query AABB; the final three are the independently fattened tree AABB.
    this.aabbPositionAttr = new StorageBufferAttribute(
      new Float32Array(maxBodies * AABB_FLOATS_PER_BODY),
      1,
    );
    // Header [0..7] is written by the AABB stage; the compact leaf ids start
    // at word 8. Capacity is one entry per live body, so a correct dispatch
    // cannot overflow even when every active-tree leaf teleports.
    this.dirtyLeafAttr = new StorageBufferAttribute(
      new Uint32Array(DIRTY_HEADER_WORDS + maxBodies),
      1,
    );
    this.aabbSnapshotBuffer = this.device.createBuffer({
      label: 'Broadphase AABB Snapshot',
      size: maxBodies * AABB_FLOATS_PER_BODY * 4,
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
    });

    const indexData = new Uint32Array(maxBodies * 3);
    for (let i = 0; i < maxBodies; i++) {
      const fatVertexBase = i * AABB_VERTICES_PER_BODY + FAT_AABB_VERTEX_OFFSET;
      indexData[i * 3 + 0] = fatVertexBase;
      indexData[i * 3 + 1] = fatVertexBase + 1;
      indexData[i * 3 + 2] = fatVertexBase + 2;
    }
    this.aabbIndexBuffer = this.device.createBuffer({
      size: indexData.byteLength,
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
    });
    this.device.queue.writeBuffer(this.aabbIndexBuffer, 0, indexData);

    const configuredGravity = options?.gravity ?? [0.0, -9.81, 0.0];
    const safeGravity: [number, number, number] = [
      Number.isFinite(configuredGravity[0]) ? configuredGravity[0] : 0.0,
      Number.isFinite(configuredGravity[1]) ? configuredGravity[1] : 0.0,
      Number.isFinite(configuredGravity[2]) ? configuredGravity[2] : 0.0,
    ];

    const updateAabbShader = wgslFn(/* wgsl */`
      fn compute(
        positions: ptr<storage, array<vec4f>, read>,
        velocities: ptr<storage, array<vec4f>, read>,
        angularVelocities: ptr<storage, array<vec4f>, read>,
        quaternions: ptr<storage, array<vec4f>, read>,
        shapes: ptr<storage, array<vec4f>, read>,
        bodyState: ptr<storage, array<u32>, read>,
        aabbPositions: ptr<storage, array<f32>, read_write>,
        dirtyLeaves: ptr<storage, array<atomic<u32>>, read_write>,
        bodyCount: u32,
        aabbFrameId: u32,
        initializedBodyCount: u32,
        activeTreeBodyCount: u32,
        reuseSleepingAabbs: u32,
        aabbMargin: f32,
        aabbFatMargin: f32,
        aabbQueryHorizon: f32,
        aabbFatHorizon: f32,
        aabbGravity: vec3f,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        if (gid >= bodyCount) { return; }

        // The CPU certificate proves this state belongs to the exact current
        // body/topology/mutation generation. Sleeping bodies have immutable
        // pose and zero velocity, so their previous tight and fat bounds stay
        // conservative and must not enter the dirty-refit list.
        if (
          reuseSleepingAabbs != 0u
          && gid < initializedBodyCount
          && (bodyState[gid] & ${SLEEPING_BODY_STATE_SLEEPING_BIT}u) != 0u
        ) {
          atomicAdd(&(*dirtyLeaves)[7u], 1u);
          return;
        }

        let pos = positions[gid].xyz;
        let vel = velocities[gid].xyz;
        let angularVel = angularVelocities[gid].xyz;
        let bodyGravity = select(vec3f(0.0), aabbGravity, positions[gid].w > 0.0);
        let q = quaternions[gid];
        let shape = shapes[gid];
        let half = shape.yzw;
        let shapeType = decodeShapeType(shape.x);
        let base = gid * ${AABB_FLOATS_PER_BODY}u;

        // Keep BVH input finite; invalid values can poison the builder.
        if (
          !isFinite3(pos)
          || !isFinite3(vel)
          || !isFinite3(angularVel)
          || !isFinite4(q)
          || !isFinite3(half)
        ) {
          let safe = vec3f(0.0, -1e6, 0.0);
          aabbPositions[base + 0u] = safe.x;
          aabbPositions[base + 1u] = safe.y;
          aabbPositions[base + 2u] = safe.z;
          aabbPositions[base + 3u] = safe.x;
          aabbPositions[base + 4u] = safe.y;
          aabbPositions[base + 5u] = safe.z;
          aabbPositions[base + 6u] = safe.x;
          aabbPositions[base + 7u] = safe.y;
          aabbPositions[base + 8u] = safe.z;
          aabbPositions[base + 9u] = safe.x;
          aabbPositions[base + 10u] = safe.y;
          aabbPositions[base + 11u] = safe.z;
          aabbPositions[base + 12u] = safe.x;
          aabbPositions[base + 13u] = safe.y;
          aabbPositions[base + 14u] = safe.z;
          aabbPositions[base + 15u] = safe.x;
          aabbPositions[base + 16u] = safe.y;
          aabbPositions[base + 17u] = safe.z;
          appendDirtyLeaf(dirtyLeaves, gid, activeTreeBodyCount, true, false);
          atomicStore(&(*dirtyLeaves)[1u], 1u);
          return;
        }

        // The query box covers the next physics step. The tree box covers a
        // longer horizon and a small independent shell. Candidate traversal
        // uses tight-vs-fat, then tight-vs-tight at leaves.
        let qSafe = normalizeSafe(q);
        let halfSafe = max(half, vec3f(0.0));
        let tight = sweptShapeBounds(
          pos,
          qSafe,
          vel,
          angularVel,
          bodyGravity,
          halfSafe,
          shapeType,
          aabbQueryHorizon,
        );
        let tightMargin = vec3f(aabbMargin);
        let sweptMin = tight.min - tightMargin;
        let sweptMax = tight.max + tightMargin;
        let sweptCenter = 0.5 * (sweptMin + sweptMax);

        aabbPositions[base + 0u] = sweptMin.x;
        aabbPositions[base + 1u] = sweptMin.y;
        aabbPositions[base + 2u] = sweptMin.z;

        aabbPositions[base + 3u] = sweptMax.x;
        aabbPositions[base + 4u] = sweptMax.y;
        aabbPositions[base + 5u] = sweptMax.z;

        aabbPositions[base + 6u] = sweptCenter.x;
        aabbPositions[base + 7u] = sweptCenter.y;
        aabbPositions[base + 8u] = sweptCenter.z;

        let oldFatMin = vec3f(
          aabbPositions[base + 9u],
          aabbPositions[base + 10u],
          aabbPositions[base + 11u],
        );
        let oldFatMax = vec3f(
          aabbPositions[base + 12u],
          aabbPositions[base + 13u],
          aabbPositions[base + 14u],
        );
        let oldFatValid = isFinite3(oldFatMin)
          && isFinite3(oldFatMax)
          && all(oldFatMax >= oldFatMin);
        let newLeaf = gid >= initializedBodyCount;
        let contained = !newLeaf && oldFatValid
          && all(sweptMin >= oldFatMin)
          && all(sweptMax <= oldFatMax);

        // Computing a swept box for an oriented body includes quaternion
        // exponentials, trigonometry, six rotations, and quadratic extrema.
        // Most leaves remain inside their old fat box, so defer that second
        // expensive sweep until an escape/new leaf actually needs a write.
        if (!contained) {
          let fat = sweptShapeBounds(
            pos,
            qSafe,
            vel,
            angularVel,
            bodyGravity,
            halfSafe,
            shapeType,
            max(aabbQueryHorizon, aabbFatHorizon),
          );
          let fatMargin = vec3f(aabbMargin + aabbFatMargin);
          let fatMin = fat.min - fatMargin;
          let fatMax = fat.max + fatMargin;
          let fatCenter = 0.5 * (fatMin + fatMax);
          aabbPositions[base + 9u] = fatMin.x;
          aabbPositions[base + 10u] = fatMin.y;
          aabbPositions[base + 11u] = fatMin.z;
          aabbPositions[base + 12u] = fatMax.x;
          aabbPositions[base + 13u] = fatMax.y;
          aabbPositions[base + 14u] = fatMax.z;
          aabbPositions[base + 15u] = fatCenter.x;
          aabbPositions[base + 16u] = fatCenter.y;
          aabbPositions[base + 17u] = fatCenter.z;
          appendDirtyLeaf(dirtyLeaves, gid, activeTreeBodyCount, true, false);
          return;
        }

        // Shrink is only a traversal-quality optimization; old fat bounds are
        // already conservative. Stagger it over 16 deterministic phases so a
        // stopped crowd cannot turn one frame into a dense refit spike.
        if (((gid + aabbFrameId) & 15u) != 0u) { return; }

        let oldArea = surfaceArea(oldFatMin, oldFatMax);
        // The real longer-horizon fat box contains tight expanded by the
        // independent shell below. Its area is therefore at least cheapArea.
        // Passing this lower-bound test proves the exact shrink test cannot
        // fire and avoids the second sweep without changing any decision.
        let cheapMin = sweptMin - vec3f(aabbFatMargin);
        let cheapMax = sweptMax + vec3f(aabbFatMargin);
        let cheapArea = surfaceArea(cheapMin, cheapMax);
        if (oldArea <= max(cheapArea * 2.0, cheapArea + 1e-8)) {
          return;
        }

        let fat = sweptShapeBounds(
          pos,
          qSafe,
          vel,
          angularVel,
          bodyGravity,
          halfSafe,
          shapeType,
          max(aabbQueryHorizon, aabbFatHorizon),
        );
        let fatMargin = vec3f(aabbMargin + aabbFatMargin);
        let fatMin = fat.min - fatMargin;
        let fatMax = fat.max + fatMargin;
        let fatCenter = 0.5 * (fatMin + fatMax);
        let proposedArea = surfaceArea(fatMin, fatMax);
        // Retain a comfortably enclosing fat box, but refresh one that has
        // become over twice as loose as the current conservative target.
        // This hysteresis avoids both per-frame leaf churn and permanently
        // huge leaves after a fast body comes to rest.
        let shrinkRefresh = oldArea > max(proposedArea * 2.0, proposedArea + 1e-8);
        if (shrinkRefresh) {
          aabbPositions[base + 9u] = fatMin.x;
          aabbPositions[base + 10u] = fatMin.y;
          aabbPositions[base + 11u] = fatMin.z;
          aabbPositions[base + 12u] = fatMax.x;
          aabbPositions[base + 13u] = fatMax.y;
          aabbPositions[base + 14u] = fatMax.z;
          aabbPositions[base + 15u] = fatCenter.x;
          aabbPositions[base + 16u] = fatCenter.y;
          aabbPositions[base + 17u] = fatCenter.z;
          appendDirtyLeaf(dirtyLeaves, gid, activeTreeBodyCount, false, true);
        }
      }

      fn appendDirtyLeaf(
        dirtyLeaves: ptr<storage, array<atomic<u32>>, read_write>,
        body: u32,
        activeTreeBodyCount: u32,
        escaped: bool,
        shrinkRefresh: bool,
      ) {
        atomicSub(&(*dirtyLeaves)[2u], 1u);
        atomicAdd(&(*dirtyLeaves)[3u], 1u);
        if (escaped) { atomicAdd(&(*dirtyLeaves)[5u], 1u); }
        if (shrinkRefresh) { atomicAdd(&(*dirtyLeaves)[6u], 1u); }
        // Appended-tail bodies are covered by the candidate exact path and are
        // intentionally absent from the completed-tree refit list.
        if (body >= activeTreeBodyCount) { return; }
        let writeIndex = atomicAdd(&(*dirtyLeaves)[0u], 1u);
        if (writeIndex < ${maxBodies}u) {
          atomicStore(&(*dirtyLeaves)[${DIRTY_HEADER_WORDS}u + writeIndex], body);
        } else {
          atomicStore(&(*dirtyLeaves)[1u], 1u);
          atomicStore(&(*dirtyLeaves)[4u], 1u);
        }
      }

      const SHAPE_TYPE_SHIFT: u32 = 14u;
      const SHAPE_TYPE_MASK: u32 = 0x3u;
      const SHAPE_TYPE_SPHERE: u32 = 1u;

      fn decodeShapeType(shapeMeta: f32) -> u32 {
        return (bitcast<u32>(shapeMeta) >> SHAPE_TYPE_SHIFT) & SHAPE_TYPE_MASK;
      }

      fn isFinite3(v: vec3f) -> bool {
        let nonNan = all(v == v);
        let bounded = all(abs(v) <= vec3f(1e20));
        return nonNan && bounded;
      }

      fn isFinite4(v: vec4f) -> bool {
        let nonNan = all(v == v);
        let bounded = all(abs(v) <= vec4f(1e20));
        return nonNan && bounded;
      }

      fn qrot(q: vec4f, v: vec3f) -> vec3f {
        let t = 2.0 * cross(q.xyz, v);
        return v + q.w * t + cross(q.xyz, t);
      }

      fn surfaceArea(boundsMin: vec3f, boundsMax: vec3f) -> f32 {
        if (!isFinite3(boundsMin) || !isFinite3(boundsMax) || any(boundsMax < boundsMin)) {
          return 1e30;
        }
        let extent = max(boundsMax - boundsMin, vec3f(0.0));
        return 2.0 * (extent.x * extent.y + extent.y * extent.z + extent.z * extent.x);
      }

      fn qmul(a: vec4f, b: vec4f) -> vec4f {
        return vec4f(
          a.w * b.xyz + b.w * a.xyz + cross(a.xyz, b.xyz),
          a.w * b.w - dot(a.xyz, b.xyz),
        );
      }

      struct SweptBounds {
        min: vec3f,
        max: vec3f,
      };

      fn normalizeSafe(q: vec4f) -> vec4f {
        let len2 = dot(q, q);
        if (len2 <= 1e-20 || !isFinite4(q)) {
          return vec4f(0.0, 0.0, 0.0, 1.0);
        }
        return q * inverseSqrt(len2);
      }

      fn integrateQuaternionExp(q: vec4f, angularVelocity: vec3f, horizon: f32) -> vec4f {
        let speed = length(angularVelocity);
        let theta = speed * max(horizon, 0.0);
        if (speed <= 1e-12 || theta <= 1e-12) {
          return q;
        }
        let halfTheta = 0.5 * theta;
        let delta = vec4f(
          angularVelocity * (sin(halfTheta) / speed),
          cos(halfTheta),
        );
        return normalizeSafe(qmul(delta, q));
      }

      fn sweptShapeBounds(
        pos: vec3f,
        q: vec4f,
        velocity: vec3f,
        angularVelocity: vec3f,
        gravity: vec3f,
        half: vec3f,
        shapeType: u32,
        horizon: f32,
      ) -> SweptBounds {
        let boundedHorizon = max(horizon, 0.0);
        let centerBounds = quadraticCenterBounds(
          pos,
          velocity,
          gravity,
          boundedHorizon,
        );
        var result: SweptBounds;
        if (shapeType == SHAPE_TYPE_SPHERE) {
          let extent = vec3f(max(half.x, 0.0));
          result.min = centerBounds.min - extent;
          result.max = centerBounds.max + extent;
          return result;
        }

        let radius = length(half);
        let theta = length(angularVelocity) * boundedHorizon;
        // At or above pi the endpoint/chord construction is no longer a
        // conservative envelope for the complete arc. The orientation sphere
        // is deliberately broader, but it cannot miss any intermediate pose.
        if (theta >= 3.141592653589793) {
          let sphereExtent = vec3f(radius);
          result.min = centerBounds.min - sphereExtent;
          result.max = centerBounds.max + sphereExtent;
          return result;
        }

        let predictedQ = integrateQuaternionExp(q, angularVelocity, boundedHorizon);
        let currentExtent = boxAabbExtent(q, half);
        let predictedExtent = boxAabbExtent(predictedQ, half);
        // Every point arc lies within this isotropic sagitta of its endpoint
        // chord for theta < pi. This bounds intermediate rotation, rather than
        // merely sampling the two endpoint orientations.
        let chordPad = radius * (1.0 - cos(0.5 * theta));
        let pad = vec3f(chordPad);
        let orientationExtent = max(currentExtent, predictedExtent) + pad;
        result.min = centerBounds.min - orientationExtent;
        result.max = centerBounds.max + orientationExtent;
        return result;
      }

      fn quadraticCenterBounds(
        pos: vec3f,
        velocity: vec3f,
        gravity: vec3f,
        horizon: f32,
      ) -> SweptBounds {
        let end = pos + velocity * horizon + gravity * (horizon * horizon);
        let linearEnd = pos + velocity * horizon;
        var result: SweptBounds;
        // Union scale=0 and scale=1. For every fixed t, all configured
        // positionGravityScale values in [0,1] lie componentwise between
        // these two curves, so their complete trajectories are enclosed too.
        result.min = min(pos, min(linearEnd, end));
        result.max = max(pos, max(linearEnd, end));
        // Per-axis extrema of p + v*t + g*t^2. positionGravityScale is at
        // most one in IntegrationStage, so using the full configured gravity
        // remains conservative for the inertial endpoint.
        for (var axis = 0u; axis < 3u; axis++) {
          let g = gravity[axis];
          if (abs(g) > 1e-12) {
            let extremumTime = -velocity[axis] / (2.0 * g);
            if (extremumTime > 0.0 && extremumTime < horizon) {
              let value = pos[axis]
                + velocity[axis] * extremumTime
                + g * extremumTime * extremumTime;
              result.min[axis] = min(result.min[axis], value);
              result.max[axis] = max(result.max[axis], value);
            }
          }
        }
        return result;
      }

      fn boxAabbExtent(q: vec4f, half: vec3f) -> vec3f {
        let ax0 = qrot(q, vec3f(1.0, 0.0, 0.0));
        let ax1 = qrot(q, vec3f(0.0, 1.0, 0.0));
        let ax2 = qrot(q, vec3f(0.0, 0.0, 1.0));
        return vec3f(
          abs(ax0.x) * half.x + abs(ax1.x) * half.y + abs(ax2.x) * half.z,
          abs(ax0.y) * half.x + abs(ax1.y) * half.y + abs(ax2.y) * half.z,
          abs(ax0.z) * half.x + abs(ax1.z) * half.y + abs(ax2.z) * half.z,
        );
      }
    `);

    this.updateAabbKernel = updateAabbShader({
      positions: storage(positions, 'vec4f', maxBodies).toReadOnly(),
      velocities: storage(velocities, 'vec4f', maxBodies).toReadOnly(),
      angularVelocities: storage(angularVelocities, 'vec4f', maxBodies).toReadOnly(),
      quaternions: storage(quaternions, 'vec4f', maxBodies).toReadOnly(),
      shapes: storage(shapes, 'vec4f', maxBodies).toReadOnly(),
      bodyState: storage(this.aabbSleepingBodyStateAttr, 'uint', maxBodies).toReadOnly(),
      aabbPositions: storage(
        this.aabbPositionAttr,
        'float',
        maxBodies * AABB_FLOATS_PER_BODY,
      ),
      dirtyLeaves: storage(
        this.dirtyLeafAttr,
        'uint',
        DIRTY_HEADER_WORDS + maxBodies,
      ).toAtomic(),
      bodyCount: uniform(0),
      aabbFrameId: uniform(0),
      initializedBodyCount: uniform(0),
      activeTreeBodyCount: uniform(0),
      reuseSleepingAabbs: uniform(0),
      // Pair-key manifolds now persist independently of broadphase rank, so
      // the BVH only needs a narrow numerical/contact margin. The previous
      // 1 cm shell tripled candidate counts in dense stacks and exhausted the
      // compact manifold pool with pairs that could not generate a contact.
      aabbMargin: uniform(0.001),
      aabbFatMargin: uniform(0.002),
      aabbQueryHorizon: uniform(1.0 / 60.0),
      aabbFatHorizon: uniform(2.0 / 60.0),
      aabbGravity: uniform(new THREE.Vector3(...safeGravity)),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('Broadphase Update AABBs');
    const bootstrapShader = wgslFn(/* wgsl */`
      fn compute(
        pairCandidateIndices: ptr<storage, array<u32>, read_write>,
        pairVisitedBits: ptr<storage, array<u32>, read_write>,
      ) -> void {
        // Force renderer-managed storage allocation for broadphase-only buffers.
        pairCandidateIndices[0] = pairCandidateIndices[0];
        pairVisitedBits[0] = pairVisitedBits[0];
      }
    `);

    this.bootstrapKernel = bootstrapShader({
      pairCandidateIndices: storage(pairCandidateIndices, 'uint', pairCandidateIndices.count),
      pairVisitedBits: storage(pairVisitedBits, 'uint', pairVisitedBits.count),
    }).computeKernel([1, 1, 1]).setName('Broadphase Bootstrap Buffers');

    this.candidateUniformBuffer = this.device.createBuffer({
      label: 'Broadphase Candidate Uniforms',
      size: 64,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });
    // Candidate appends are one of the densest atomic hot spots in the 50K and
    // 64K worlds. Never share their cache line with the cross-stage safety
    // gate: doing so serializes unrelated fatal checks and downstream reads.
    // A one-thread publication pass copies only the retained-prefix result
    // back to safety word 1 after candidate generation has finished.
    this.candidateCounterBuffer = this.device.createBuffer({
      label: 'Broadphase Candidate Counter',
      size: 4,
      usage: GPUBufferUsage.STORAGE,
    });
    this.debugCountersBuffer = this.device.createBuffer({
      label: 'Broadphase Debug Counters',
      size: DEBUG_COUNTER_WORDS * 4,
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC | GPUBufferUsage.COPY_DST,
    });
    this.debugReadbackBuffer = this.createDebugReadbackBuffer();
    for (let i = 0; i < FATAL_SAMPLE_RING_SIZE; i++) {
      this.fatalSampleSlots.push(this.createFatalSampleSlot());
    }

    this.candidateBindGroupLayout = this.device.createBindGroupLayout({
      label: 'Broadphase Candidate BindGroupLayout',
      entries: [
        { binding: 0, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'uniform' } },
        { binding: 1, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'read-only-storage' } },
        { binding: 2, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'read-only-storage' } },
        { binding: 3, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'read-only-storage' } },
        { binding: 4, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'storage' } },
        { binding: 5, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'storage' } },
        { binding: 6, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'storage' } },
        { binding: 7, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'storage' } },
        { binding: 8, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'read-only-storage' } },
        { binding: 9, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'read-only-storage' } },
        { binding: 10, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'read-only-storage' } },
      ],
    });

    this.publishCandidateSafetyBindGroupLayout = this.device.createBindGroupLayout({
      label: 'Broadphase Candidate Safety Publish BindGroupLayout',
      entries: [
        { binding: 0, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'storage' } },
        { binding: 1, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'storage' } },
        { binding: 2, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'storage' } },
      ],
    });

    const candidatePublicationWgsl = createBroadphaseCandidatePublicationWgsl(
      this.candidateEndpointLayout,
    );
    const wideIgnoredPairGuardWgsl = this.pairEndpointEncoding === 'wide-u32'
      ? createBroadphaseWideIgnoredPairGuardWgsl()
      : '';
    const candidateShaderModule = this.device.createShaderModule({
      label: 'Broadphase Candidate Shader',
      code: `
struct Uniforms {
  bodyCount: u32,
  pairCapacity: u32,
  visitedWordCount: u32,
  pairsPerBody: u32,
  bvhNodeCapacity: u32,
  useVisitedDedup: u32,
  debugEnabled: u32,
  ignoredPairHashCapacity: u32,
  bvhBodyCount: u32,
  useStacklessTraversal: u32,
  forceTopologyFatal: u32,
  sparseSleepingMode: u32,
  retainedPairScratchOffset: u32,
  awakeBodyScratchOffset: u32,
  awakeBodyBitsetOffset: u32,
};

struct BVH2Node {
  boundsMin: vec3f,
  leftChild: u32,
  boundsMax: vec3f,
  rightChild: u32,
};

@group(0) @binding(0) var<uniform> uniforms: Uniforms;
@group(0) @binding(1) var<storage, read> aabbPositions: array<f32>;
@group(0) @binding(2) var<storage, read> bvhNodes: array<BVH2Node>;
  @group(0) @binding(3) var<storage, read> parentIdx: array<u32>;
@group(0) @binding(4) var<storage, read_write> pairActivity: array<u32>;
@group(0) @binding(5) var<storage, read_write> pairVisitedBits: array<atomic<u32>>;
@group(0) @binding(6) var<storage, read_write> candidateState: array<atomic<u32>>;
@group(0) @binding(7) var<storage, read_write> debugCounters: array<atomic<u32>>;
@group(0) @binding(8) var<storage, read> positions: array<vec4f>;
@group(0) @binding(9) var<storage, read> shapes: array<vec4f>;
@group(0) @binding(10) var<storage, read> ignoredPairHash: array<u32>;

// Dense production worlds compile the same entry points with this override at
// zero. Dawn can then remove every sleeping-sparse branch from the hot BVH
// traversal without maintaining a second source implementation.
override SPARSE_CAPABLE: u32 = 1u;

const INVALID_IDX: u32 = 0xFFFFFFFFu;
const INVALID_PAIR: u32 = 0xFFFFFFFFu;

fn overlaps(aMin: vec3f, aMax: vec3f, bMin: vec3f, bMax: vec3f) -> bool {
  return !(aMax.x < bMin.x || aMin.x > bMax.x ||
           aMax.y < bMin.y || aMin.y > bMax.y ||
           aMax.z < bMin.z || aMin.z > bMax.z);
}

fn pairIndex(i: u32, j: u32) -> u32 {
  return (j * (j - 1u)) / 2u + i;
}

fn hashIgnoredPairKey(key: u32) -> u32 {
  var value = key;
  value ^= value >> 16u;
  value *= 0x7feb352du;
  value ^= value >> 15u;
  value *= 0x846ca68bu;
  return value ^ (value >> 16u);
}

fn isIgnoredPair(i: u32, j: u32) -> bool {
${wideIgnoredPairGuardWgsl}  let key = (i & 0xffffu) | ((j & 0xffffu) << 16u);
  let mask = uniforms.ignoredPairHashCapacity - 1u;
  var slot = hashIgnoredPairKey(key) & mask;
  var probe = 0u;
  loop {
    // The table is provisioned sparsely, so a 128-slot cluster is already a
    // corrupt/pathological state. Never let a malformed ignored-pair table
    // turn one candidate into an effectively unbounded GPU loop. Treat an
    // unresolved lookup as collidable: an extra contact is conservative,
    // whereas suppressing it would be an unrecoverable same-frame pair loss.
    if (probe >= min(uniforms.ignoredPairHashCapacity, 128u)) {
      atomicAdd(&debugCounters[11u], 1u);
      return false;
    }
    let base = slot * 2u;
    let state = ignoredPairHash[base + 1u];
    if (state == 0u) { return false; }
    if (state == 1u && ignoredPairHash[base] == key) { return true; }
    slot = (slot + 1u) & mask;
    probe += 1u;
  }

}

fn decodeShapeCollisionGroup(shapeMeta: f32) -> u32 {
  return (bitcast<u32>(shapeMeta) >> 16u) & 0xffu;
}

fn decodeShapeCollisionMask(shapeMeta: f32) -> u32 {
  return (bitcast<u32>(shapeMeta) >> 24u) & 0xffu;
}

fn shapesCanCollide(shapeA: vec4f, shapeB: vec4f) -> bool {
  let groupA = decodeShapeCollisionGroup(shapeA.x);
  let groupB = decodeShapeCollisionGroup(shapeB.x);
  let maskA = decodeShapeCollisionMask(shapeA.x);
  let maskB = decodeShapeCollisionMask(shapeB.x);
  return (maskA & groupB) != 0u && (maskB & groupA) != 0u;
}

fn loadBodyMin(body: u32) -> vec3f {
  let base = body * ${AABB_FLOATS_PER_BODY}u;
  return vec3f(
    aabbPositions[base + 0u],
    aabbPositions[base + 1u],
    aabbPositions[base + 2u]
  );
}

fn loadBodyMax(body: u32) -> vec3f {
  let base = body * ${AABB_FLOATS_PER_BODY}u;
  return vec3f(
    aabbPositions[base + 3u],
    aabbPositions[base + 4u],
    aabbPositions[base + 5u]
  );
}

fn loadBodyCenter(body: u32) -> vec3f {
  let base = body * ${AABB_FLOATS_PER_BODY}u;
  return vec3f(
    aabbPositions[base + 6u],
    aabbPositions[base + 7u],
    aabbPositions[base + 8u]
  );
}

@compute @workgroup_size(1)
fn clearCounter() {
  atomicStore(&candidateState[0u], 0u);
  pairActivity[0] = 0u;
  // Counter 0 and the cumulative traversal counters 9/10/11 are monotonic
  // scene diagnostics.  Per-topology counters 3/7 are cleared only after a
  // replacement topology has been published.
  atomicStore(&debugCounters[1u], 0u);
  atomicStore(&debugCounters[2u], 0u);
  atomicStore(&debugCounters[4u], 0u);
  atomicStore(&debugCounters[5u], 0u);
  // 8 is a per-dispatch cooperative abort flag. Unlike the sticky correctness
  // counters above, it must be cleared for each new candidate pass.
  atomicStore(&debugCounters[8u], 0u);
  // Freeze one mode for the entire emit dispatch. Counter 6 is scratch until
  // finalizeCounter overwrites it with the published compact count:
  //   0 = BVH traversal, 1 = bounded exact recovery, 2 = reject/rebuild.
  // Without this snapshot, a traversal fault raised by one workgroup could
  // make later workgroups enter exact mode after earlier ones emitted BVH
  // pairs, producing a mixed/partial candidate stream.
  if (uniforms.forceTopologyFatal != 0u) {
    atomicStore(&debugCounters[${REFIT_FATAL_COUNTER}u], 1u);
  }
  let topologyFatal = uniforms.forceTopologyFatal != 0u
    || atomicLoad(&debugCounters[${REFIT_FATAL_COUNTER}u]) != 0u;
  let boundedExact = topologyFatal
    && uniforms.bodyCount <= ${BROADPHASE_FATAL_EXACT_FALLBACK_BODY_LIMIT}u;
  let rejectFatal = topologyFatal && !boundedExact;
  var dispatchMode = select(0u, 1u, boundedExact);
  dispatchMode = select(dispatchMode, 2u, rejectFatal);
  atomicStore(&debugCounters[6u], dispatchMode);
  if (rejectFatal) {
    atomicStore(&debugCounters[8u], 1u);
    atomicStore(&debugCounters[${FATAL_CUMULATIVE_COUNTER}u], 1u);
  }
}

fn sparseValidatorFaulted() -> bool {
  return SPARSE_CAPABLE != 0u
    && uniforms.sparseSleepingMode != 0u
    && pairActivity[
      uniforms.awakeBodyBitsetOffset + ${Math.max(1, Math.ceil(this.maxBodies / 32))}u
    ] != 0u;
}

fn otherCanOwnDynamicPair(other: u32, sparseHealthy: bool) -> bool {
  if (positions[other].w <= 0.0) { return false; }
  if (SPARSE_CAPABLE == 0u || !sparseHealthy) { return true; }
  let word = other >> 5u;
  let bit = 1u << (other & 31u);
  return (pairActivity[uniforms.awakeBodyBitsetOffset + word] & bit) != 0u;
}

@compute @workgroup_size(${CANDIDATE_WORKGROUP_SIZE})
fn clearVisited(@builtin(global_invocation_id) globalId: vec3u) {
  if (uniforms.useVisitedDedup == 0u) { return; }
  let idx = globalId.x;
  if (idx >= uniforms.visitedWordCount) { return; }
  atomicStore(&pairVisitedBits[idx], 0u);
}

fn appendCandidate(body: u32, other: u32) {
  if (body == other || body >= uniforms.bodyCount || other >= uniforms.bodyCount) { return; }
  let bodyDynamic = positions[body].w > 0.0;
  let otherDynamic = positions[other].w > 0.0;
  if (!bodyDynamic && !otherDynamic) { return; }
  if (uniforms.debugEnabled > 0u) {
    atomicAdd(&debugCounters[4u], 1u);
  }

  let a = min(body, other);
  let b = max(body, other);
  if (!shapesCanCollide(shapes[a], shapes[b])) { return; }
  if (isIgnoredPair(a, b)) { return; }

  let pair = pairIndex(a, b);
  if (uniforms.useVisitedDedup != 0u) {
    let word = pair >> 5u;
    let bit = 1u << (pair & 31u);
    let previous = atomicOr(&pairVisitedBits[word], bit);
    if ((previous & bit) != 0u) {
      if (uniforms.debugEnabled > 0u) {
        atomicAdd(&debugCounters[1u], 1u);
      }
      return;
    }
  }

${candidatePublicationWgsl}
}

@compute @workgroup_size(${CANDIDATE_WORKGROUP_SIZE})
fn seedRetainedPairs(@builtin(global_invocation_id) globalId: vec3u) {
  if (uniforms.sparseSleepingMode == 0u || sparseValidatorFaulted()) { return; }
  let gid = globalId.x;
  let retainedCount = min(
    pairActivity[uniforms.retainedPairScratchOffset],
    uniforms.pairCapacity,
  );
  if (gid >= retainedCount) { return; }
  // retainSleepingPairs already wrote the packed pair into the deterministic
  // candidate prefix and kept its descriptor poolIndex in the aligned scratch
  // row. Publish that prefix in one cold store instead of re-appending every
  // retained pair through the hot candidate atomic/hash/filter path.
  if (gid == 0u) {
    atomicStore(&candidateState[0u], retainedCount);
  }
  if (uniforms.debugEnabled > 0u) {
    atomicAdd(&debugCounters[2u], 1u);
  }
}

@compute @workgroup_size(1)
fn captureRetainedPrefix() {
  let prefix = select(
    0u,
    min(atomicLoad(&candidateState[0u]), uniforms.pairCapacity),
    uniforms.sparseSleepingMode != 0u && !sparseValidatorFaulted(),
  );
  pairActivity[
    uniforms.awakeBodyBitsetOffset + ${Math.max(1, Math.ceil(this.maxBodies / 32)) + 1}u
  ] = prefix;
}

@compute @workgroup_size(${CANDIDATE_WORKGROUP_SIZE})
fn emitPairs(@builtin(global_invocation_id) globalId: vec3u) {
  let queryIndex = globalId.x;
  let sparseHealthy = SPARSE_CAPABLE != 0u
    && uniforms.sparseSleepingMode != 0u
    && !sparseValidatorFaulted();
  if (
    sparseHealthy
    && queryIndex >= pairActivity[uniforms.awakeBodyScratchOffset]
  ) { return; }
  var body = queryIndex;
  if (sparseHealthy) {
    body = pairActivity[uniforms.awakeBodyScratchOffset + queryIndex + 1u];
  }
  if (body >= uniforms.bodyCount) { return; }

  let aabbMin = loadBodyMin(body);
  let aabbMax = loadBodyMax(body);
  let bodyDynamic = positions[body].w > 0.0;
  let bvhBodyCount = min(uniforms.bvhBodyCount, uniforms.bodyCount);
  let dispatchMode = atomicLoad(&debugCounters[6u]);

  // clearCounter snapshots the post-refit fault state before any emit
  // invocation runs. Small worlds retain a bounded exact recovery; large
  // worlds do O(1) work per invocation and publish zero candidates while an
  // asynchronous replacement topology is requested from the sampled latch.
  if (dispatchMode == 2u) { return; }
  if (dispatchMode == 1u) {
    for (var other = body + 1u; other < uniforms.bodyCount; other++) {
      if ((other & 31u) == 0u && atomicLoad(&debugCounters[8u]) != 0u) { return; }
      if (!bodyDynamic && positions[other].w <= 0.0) { continue; }
      if (overlaps(aabbMin, aabbMax, loadBodyMin(other), loadBodyMax(other))) {
        appendCandidate(body, other);
      }
    }
    return;
  }

  // Traverse the last completed topology for all dynamic bodies and for every
  // appended static body. A static tail body is absent from the old tree, so
  // it owns its dynamic-old pairs just like a dynamic tail body. Traversing
  // the tree keeps that path O(log N + overlaps); the former linear scan was
  // O(staticDelta * oldBodyCount) and could freeze a 64k prefix.
  let traversesCompletedTree = bodyDynamic || body >= bvhBodyCount;
  if (traversesCompletedTree && bvhBodyCount > 0u) {
    let maxNodes = uniforms.bvhNodeCapacity;
    let root = select(0u, bvhBodyCount, bvhBodyCount > 1u);
    if (uniforms.useStacklessTraversal == 0u) {
      // The builder's measured depth proves this fixed private stack is large
      // enough for the active immutable topology. Unlike the parent walk, each
      // visited BVH node is fetched exactly once.
      var stack: array<u32, ${PAIR_STACK_SIZE}>;
      var sp = 0u;
      var visitCount = 0u;
      let visitBudget = max(1u, maxNodes);
      var traversalFailed = false;
      if (root < maxNodes) {
        stack[0u] = root;
        sp = 1u;
      } else {
        traversalFailed = true;
      }

      loop {
        if (sp == 0u || traversalFailed) { break; }
        if (visitCount >= visitBudget) {
          atomicAdd(&debugCounters[3u], 1u);
          atomicAdd(&debugCounters[9u], 1u);
          traversalFailed = true;
          break;
        }
        if (
          visitCount > 0u
          && (visitCount & 255u) == 0u
          && atomicLoad(&debugCounters[8u]) != 0u
        ) {
          return;
        }
        visitCount += 1u;
        sp -= 1u;
        let nodeIdx = stack[sp];
        if (nodeIdx >= maxNodes) {
          traversalFailed = true;
          break;
        }

        let node = bvhNodes[nodeIdx];
        if (!overlaps(aabbMin, aabbMax, node.boundsMin, node.boundsMax)) { continue; }
        if (node.leftChild == INVALID_IDX) {
          let other = node.rightChild;
          if (
            other != body && other < bvhBodyCount
            && !(
              body < bvhBodyCount
              && otherCanOwnDynamicPair(other, sparseHealthy)
              && other <= body
            )
            && overlaps(aabbMin, aabbMax, loadBodyMin(other), loadBodyMax(other))
          ) {
            appendCandidate(body, other);
          }
          continue;
        }

        let left = node.leftChild;
        let right = node.rightChild;
        if (
          left == INVALID_IDX || right == INVALID_IDX || left == right
          || left == nodeIdx || right == nodeIdx
          || left >= maxNodes || right >= maxNodes
          || sp + 2u > ${PAIR_STACK_SIZE}u
        ) {
          traversalFailed = true;
          break;
        }
        stack[sp] = left;
        sp += 1u;
        stack[sp] = right;
        sp += 1u;
      }

      if (traversalFailed) {
        atomicAdd(&debugCounters[7u], 1u);
        atomicAdd(&debugCounters[10u], 1u);
        // The depth-selected path makes this unreachable for a validated tree.
        // Abort this whole candidate publication. The frozen dispatch mode
        // prevents other workgroups from switching to exact mid-dispatch.
        atomicStore(&debugCounters[${REFIT_FATAL_COUNTER}u], 1u);
        atomicStore(&debugCounters[8u], 1u);
        atomicStore(&debugCounters[${FATAL_CUMULATIVE_COUNTER}u], 1u);
        return;
      }
    } else {
    var visitCount = 0u;
    // A stackless DFS crosses every tree edge at most twice. maxNodes is 2*N,
    // so 2*maxNodes is a strict upper bound for the complete state walk.
    let visitBudget = max(1u, maxNodes * 2u);
    var traversalFailed = false;
    var current = root;
    var previous = INVALID_IDX;
    if (root >= maxNodes) {
      atomicAdd(&debugCounters[7u], 1u);
      atomicAdd(&debugCounters[10u], 1u);
      traversalFailed = true;
    }

    loop {
      if (traversalFailed) { break; }
      if (visitCount >= visitBudget) {
        atomicAdd(&debugCounters[3u], 1u);
        atomicAdd(&debugCounters[9u], 1u);
        traversalFailed = true;
        break;
      }
      if (
        visitCount > 0u
        && (visitCount & 255u) == 0u
        && atomicLoad(&debugCounters[8u]) != 0u
      ) {
        return;
      }
      visitCount += 1u;
      if (current >= maxNodes) {
        atomicAdd(&debugCounters[7u], 1u);
        atomicAdd(&debugCounters[10u], 1u);
        traversalFailed = true;
        break;
      }

      let node = bvhNodes[current];
      let parent = parentIdx[current];
      let entering = previous == parent;
      var next = INVALID_IDX;

      if (entering) {
        if (!overlaps(aabbMin, aabbMax, node.boundsMin, node.boundsMax)) {
          next = parent;
        } else if (node.leftChild == INVALID_IDX) {
          let other = node.rightChild;
          if (other != body && other < bvhBodyCount) {
            // Old-vs-old dynamic pairs are emitted by their lower body id. A
            // tail body owns every pair against the completed-tree prefix.
            // Tree leaves carry fat bounds, so finish with tight-tight overlap.
            if (
              !(
                body < bvhBodyCount
                && otherCanOwnDynamicPair(other, sparseHealthy)
                && other <= body
              )
              && overlaps(aabbMin, aabbMax, loadBodyMin(other), loadBodyMax(other))
            ) {
              appendCandidate(body, other);
            }
          }
          next = parent;
        } else {
          let left = node.leftChild;
          let right = node.rightChild;
          if (
            left == INVALID_IDX || right == INVALID_IDX || left == right
            || left == current || right == current
            || left >= maxNodes || right >= maxNodes
            || parentIdx[left] != current || parentIdx[right] != current
          ) {
            traversalFailed = true;
          } else {
            next = left;
          }
        }
      } else if (node.leftChild != INVALID_IDX && previous == node.leftChild) {
        next = node.rightChild;
      } else if (node.leftChild != INVALID_IDX && previous == node.rightChild) {
        next = parent;
      } else {
        traversalFailed = true;
      }

      if (traversalFailed) {
        atomicAdd(&debugCounters[7u], 1u);
        atomicAdd(&debugCounters[10u], 1u);
        break;
      }

      if (next == INVALID_IDX) {
        if (current == root && parent == INVALID_IDX) { break; }
        atomicAdd(&debugCounters[7u], 1u);
        atomicAdd(&debugCounters[10u], 1u);
        traversalFailed = true;
        break;
      }
      previous = current;
      current = next;
    }

    if (traversalFailed) {
      // The first-frame validator makes this unreachable for a valid immutable
      // topology. Abort this whole publication and latch the fault for the
      // bounded-exact-or-rebuild decision in the following dispatch.
      atomicStore(&debugCounters[${REFIT_FATAL_COUNTER}u], 1u);
      atomicStore(&debugCounters[8u], 1u);
      atomicStore(&debugCounters[${FATAL_CUMULATIVE_COUNTER}u], 1u);
      return;
    }
    }
  }

  if (body >= bvhBodyCount) {
    // Cover the small appended tail exactly. Rebuilds are latest-wins, so this
    // O(delta^2) path stays bounded by bodies added during one asynchronous
    // build and removes body-count starvation without weakening collisions.
    for (var other = body + 1u; other < uniforms.bodyCount; other++) {
      if ((other & 31u) == 0u && atomicLoad(&debugCounters[8u]) != 0u) {
        return;
      }
      if (!bodyDynamic && positions[other].w <= 0.0) { continue; }
      if (overlaps(aabbMin, aabbMax, loadBodyMin(other), loadBodyMax(other))) {
        appendCandidate(body, other);
      }
    }
  }
}

@compute @workgroup_size(1)
fn finalizeCounter() {
  let rawCount = atomicLoad(&candidateState[0u]);
  // Any capacity/traversal abort invalidates every pair written by this
  // dispatch. Publishing a zero header guarantees contact generation can
  // never consume a partial BVH prefix. A bounded exact recovery has abort=0
  // and is published normally.
  let dispatchAborted = atomicLoad(&debugCounters[8u]) != 0u;
  let publishedRawCount = select(rawCount, 0u, dispatchAborted);
  let compactCount = min(publishedRawCount, uniforms.pairCapacity);
  pairActivity[0] = compactCount;
  // These two values also power the always-on sparse capacity readback, so
  // publish them even when verbose debug logging is disabled.
  atomicStore(&debugCounters[5u], publishedRawCount);
  atomicStore(&debugCounters[6u], compactCount);
  // The dedicated counter is no longer needed after publication. Preserve the
  // accepted retained prefix so the following one-thread safety publication
  // can copy it to the shared gate without putting the hot append atomic back
  // on that gate's cache line.
  var retainedPrefix = 0u;
  if (
    SPARSE_CAPABLE != 0u
    && uniforms.sparseSleepingMode != 0u
    && !sparseValidatorFaulted()
  ) {
    retainedPrefix = min(
      pairActivity[
        uniforms.awakeBodyBitsetOffset + ${Math.max(1, Math.ceil(this.maxBodies / 32)) + 1}u
      ],
      compactCount,
    );
  }
  atomicStore(&candidateState[0u], retainedPrefix);
}
`,
    });

    const pipelineLayout = this.device.createPipelineLayout({
      bindGroupLayouts: [this.candidateBindGroupLayout],
    });

    const candidatePipelineDescriptors: GPUComputePipelineDescriptor[] = [
      {
        label: 'Broadphase Clear Counter',
        layout: pipelineLayout,
        compute: { module: candidateShaderModule, entryPoint: 'clearCounter' },
      },
      {
        label: 'Broadphase Clear Visited',
        layout: pipelineLayout,
        compute: { module: candidateShaderModule, entryPoint: 'clearVisited' },
      },
      {
        label: 'Broadphase Emit Pairs',
        layout: pipelineLayout,
        compute: { module: candidateShaderModule, entryPoint: 'emitPairs' },
      },
      {
        label: 'Broadphase Emit Pairs (Dense Specialized)',
        layout: pipelineLayout,
        compute: {
          module: candidateShaderModule,
          entryPoint: 'emitPairs',
          constants: { SPARSE_CAPABLE: 0 },
        },
      },
      {
        label: 'Broadphase Finalize Counter',
        layout: pipelineLayout,
        compute: { module: candidateShaderModule, entryPoint: 'finalizeCounter' },
      },
      {
        label: 'Broadphase Finalize Counter (Dense Specialized)',
        layout: pipelineLayout,
        compute: {
          module: candidateShaderModule,
          entryPoint: 'finalizeCounter',
          constants: { SPARSE_CAPABLE: 0 },
        },
      },
      {
        label: 'Broadphase Seed Retained Sleeping Pairs',
        layout: pipelineLayout,
        compute: { module: candidateShaderModule, entryPoint: 'seedRetainedPairs' },
      },
      {
        label: 'Broadphase Capture Retained Prefix',
        layout: pipelineLayout,
        compute: { module: candidateShaderModule, entryPoint: 'captureRetainedPrefix' },
      },
    ];

    const publishCandidateSafetyModule = this.device.createShaderModule({
      label: 'Broadphase Candidate Safety Publish Shader',
      code: `
@group(0) @binding(0) var<storage, read_write> candidateCounter: array<atomic<u32>>;
@group(0) @binding(1) var<storage, read_write> debugCounters: array<atomic<u32>>;
@group(0) @binding(2) var<storage, read_write> safetyGate: array<atomic<u32>>;

@compute @workgroup_size(1)
fn publishCandidateSafety() {
  // ContactGeneration consumes word 1 as the exact retained-sleeping prefix.
  // It is a cold publication value, not the high-contention append counter.
  atomicStore(&safetyGate[1u], atomicLoad(&candidateCounter[0u]));
  // Abort is the exact same-dispatch condition that formerly ORed the shared
  // gate at each reject/overflow/traversal-failure site. A refit-fatal alone
  // may select the complete bounded-exact recovery and is therefore not an
  // incomplete graph.
  if (atomicLoad(&debugCounters[8u]) != 0u) {
    atomicOr(&safetyGate[0u], ${CONSTRAINT_SAFETY_BROADPHASE_FATAL_BIT}u);
  }
}
`,
    });
    const publishCandidateSafetyLayout = this.device.createPipelineLayout({
      bindGroupLayouts: [this.publishCandidateSafetyBindGroupLayout],
    });
    candidatePipelineDescriptors.push({
      label: 'Broadphase Publish Candidate Safety',
      layout: publishCandidateSafetyLayout,
      compute: {
        module: publishCandidateSafetyModule,
        entryPoint: 'publishCandidateSafety',
      },
    });

    this.candidatePipelineState = 'pending';
    this.candidatePipelinePrewarm = Promise.resolve().then(async () => {
      const results = await Promise.allSettled(
        candidatePipelineDescriptors.map((descriptor) => (
          this.device.createComputePipelineAsync(descriptor)
        )),
      );
      // createComputePipelineAsync() is not cancellable. Never publish its
      // result into an instance whose resources have already been disposed.
      if (this.disposed) return;
      const rejected = results.find(
        (result): result is PromiseRejectedResult => result.status === 'rejected',
      );
      if (rejected) throw rejected.reason;
      const pipelines = results.map((result) => (
        (result as PromiseFulfilledResult<GPUComputePipeline>).value
      ));
      const [
        clearCounter,
        clearVisited,
        emitPairs,
        denseEmitPairs,
        finalizeCounter,
        denseFinalizeCounter,
        seedRetainedPairs,
        captureRetainedPrefix,
        publishCandidateSafety,
      ] = pipelines;
      this.candidatePipelines = Object.freeze({
        clearCounter,
        clearVisited,
        seedRetainedPairs,
        emitPairs,
        denseEmitPairs,
        finalizeCounter,
        denseFinalizeCounter,
        captureRetainedPrefix,
        publishCandidateSafety,
      });
      this.candidatePipelineState = 'ready';
    }).catch((error) => {
      if (this.disposed) return;
      this.candidatePipelineError = this.makeStartupPipelineError(
        'BroadPhaseStage candidate pipeline prewarm failed',
        error,
      );
      this.candidatePipelineState = 'error';
      console.error(this.candidatePipelineError);
    });
    this.appendStartupPrewarmFence(Promise.all([
      this.candidatePipelinePrewarm,
      this.lbvhPipelinePrewarm,
    ]).then(() => undefined));
  }

  setNativeComputePassDescriptorFactory(
    factory: NativeComputePassDescriptorFactory | undefined,
  ): void {
    this.nativeComputePassDescriptorFactory = factory;
  }

  private createComputePassDescriptor(label: string): GPUComputePassDescriptor {
    return this.nativeComputePassDescriptorFactory?.(label) ?? { label };
  }

  /**
   * Attaches only the CPU certificate provider needed by the per-body AABB
   * reuse path. Unlike sparse traversal, this has no contact-pool requirement.
   */
  attachSleepingAabbCertificate(
    provider: () => SleepingSparseBroadphaseCertificate,
  ): void {
    if (this.disposed) throw new Error('Cannot attach sleeping AABB state to a disposed broadphase.');
    if (this.sleepingAabbCertificateProvider) {
      throw new Error('Broadphase sleeping AABB certificate is already attached.');
    }
    this.sleepingAabbCertificateProvider = provider;
  }

  /**
   * Attaches the compact contact pool and the previously submitted sleeping
   * body state. Merely attaching does not enable sparse traversal: every
   * dispatch also requires a current CPU certificate from SleepingIslands.
   */
  attachSleepingSparseState(inputs: BroadphaseSleepingSparseInputs): void {
    if (this.disposed) throw new Error('Cannot attach sparse sleeping state to a disposed broadphase.');
    if (this.sparseSleepingInputs) {
      throw new Error('Broadphase sparse sleeping state is already attached.');
    }
    const maxPersistentManifolds = Math.floor(inputs.maxPersistentManifolds);
    const pairManifoldSlots = Math.floor(inputs.pairManifoldSlots);
    const retainedOffset = Math.floor(inputs.retainedPairScratchOffset);
    const awakeOffset = Math.floor(inputs.awakeBodyScratchOffset);
    const awakeCapacity = Math.floor(inputs.awakeBodyScratchCapacity);
    const awakeBitsetWords = Math.max(1, Math.ceil(this.maxBodies / 32));
    if (
      maxPersistentManifolds < 1
      || pairManifoldSlots < 1
      || pairManifoldSlots > 31
      || inputs.compactLayout.poolKey.length < maxPersistentManifolds
      || inputs.compactLayout.outputMask.length < maxPersistentManifolds
      || inputs.compactLayout.slotRow.length < maxPersistentManifolds * pairManifoldSlots
    ) {
      throw new Error('Broadphase sparse sleeping requires a complete compact manifold layout.');
    }
    if (
      retainedOffset < 0
      || retainedOffset + this.maxPairs + 1 > this.pairActivityAttr.count
      || awakeOffset < 0
      || awakeCapacity < this.maxBodies + awakeBitsetWords + 2
      || awakeOffset + awakeCapacity + 1 > this.pairActivityAttr.count
    ) {
      throw new Error('Broadphase sparse sleeping scratch ranges are not large enough.');
    }

    this.sparseSleepingInputs = Object.freeze({ ...inputs });
    this.sleepingAabbCertificateProvider ??= inputs.getCertificate;
    this.sparseSleepingUniformBuffer = this.device.createBuffer({
      label: 'Broadphase Sparse Sleeping Uniforms',
      // Five u32 values round up to two 16-byte uniform-alignment quanta.
      size: 32,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });
    this.sparseSleepingArgsBuffer = this.device.createBuffer({
      label: 'Broadphase Sparse Sleeping Indirect + Validation State',
      size: SPARSE_ARGS_WORDS * 4,
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.INDIRECT
        | GPUBufferUsage.COPY_SRC | GPUBufferUsage.COPY_DST,
    });
    this.sparseSleepingBindGroupLayout = this.device.createBindGroupLayout({
      label: 'Broadphase Sparse Sleeping BindGroupLayout',
      entries: [
        { binding: 0, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'uniform' } },
        { binding: 1, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'read-only-storage' } },
        { binding: 2, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'storage' } },
        { binding: 3, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'read-only-storage' } },
        { binding: 4, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'read-only-storage' } },
        { binding: 5, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'read-only-storage' } },
        { binding: 6, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'storage' } },
        { binding: 7, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'storage' } },
      ],
    });

    this.sparseSleepingWorkBindGroupLayout = this.device.createBindGroupLayout({
      label: 'Broadphase Sparse Sleeping Work BindGroupLayout',
      entries: [
        { binding: 0, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'uniform' } },
        { binding: 1, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'read-only-storage' } },
        { binding: 2, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'storage' } },
        { binding: 3, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'read-only-storage' } },
        { binding: 4, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'read-only-storage' } },
        { binding: 5, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'read-only-storage' } },
        { binding: 7, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'storage' } },
      ],
    });
    const sparseModule = this.device.createShaderModule({
      label: 'Broadphase Sparse Sleeping Query + Retained Pair Shader',
      code: `
struct SparseUniforms {
  bodyCount: u32,
  pairCapacity: u32,
  maxPersistentManifolds: u32,
  awakeBitsetWords: u32,
  bvhBodyCount: u32,
};

@group(0) @binding(0) var<uniform> uniforms: SparseUniforms;
@group(0) @binding(1) var<storage, read> aabbPositions: array<f32>;
@group(0) @binding(2) var<storage, read_write> pairActivity: array<atomic<u32>>;
@group(0) @binding(3) var<storage, read> bodyState: array<u32>;
@group(0) @binding(4) var<storage, read> positions: array<vec4f>;
@group(0) @binding(5) var<storage, read> persistentManifoldMap: array<u32>;
@group(0) @binding(6) var<storage, read_write> sparseArgs: array<atomic<u32>>;
@group(0) @binding(7) var<storage, read_write> safetyGate: array<atomic<u32>>;

const BODY_INITIALIZED: u32 = 0x00000001u;
const BODY_SLEEPING: u32 = 0x00000002u;
const RETAINED_OFFSET: u32 = ${retainedOffset}u;
const AWAKE_OFFSET: u32 = ${awakeOffset}u;
const AWAKE_BITSET_OFFSET: u32 = ${awakeOffset + 1 + this.maxBodies}u;
const VALIDATOR_FAULT_OFFSET: u32 = ${awakeOffset + 1 + this.maxBodies + awakeBitsetWords}u;
const RETAINED_PREFIX_OFFSET: u32 = ${awakeOffset + 2 + this.maxBodies + awakeBitsetWords}u;
const POOL_KEY_OFFSET: u32 = ${inputs.compactLayout.poolKey.offset}u;
const OUTPUT_MASK_OFFSET: u32 = ${inputs.compactLayout.outputMask.offset}u;
const VALID_OUTPUT_MASK: u32 = ${(2 ** pairManifoldSlots) - 1}u;

fn latchValidatorFault(reason: u32) {
  atomicOr(&pairActivity[VALIDATOR_FAULT_OFFSET], reason);
  atomicOr(&safetyGate[0u], ${CONSTRAINT_SAFETY_BROADPHASE_FATAL_BIT}u);
}

fn finiteScalar(v: f32) -> bool {
  return v == v && abs(v) <= 1e20;
}

fn loadBodyMin(body: u32) -> vec3f {
  let base = body * ${AABB_FLOATS_PER_BODY}u;
  return vec3f(aabbPositions[base], aabbPositions[base + 1u], aabbPositions[base + 2u]);
}

fn loadBodyMax(body: u32) -> vec3f {
  let base = body * ${AABB_FLOATS_PER_BODY}u;
  return vec3f(aabbPositions[base + 3u], aabbPositions[base + 4u], aabbPositions[base + 5u]);
}

fn finiteBounds(boundsMin: vec3f, boundsMax: vec3f) -> bool {
  return all(boundsMin == boundsMin) && all(boundsMax == boundsMax)
    && all(abs(boundsMin) <= vec3f(1e20)) && all(abs(boundsMax) <= vec3f(1e20))
    && all(boundsMax >= boundsMin);
}

fn overlaps(aMin: vec3f, aMax: vec3f, bMin: vec3f, bMax: vec3f) -> bool {
  return !(aMax.x < bMin.x || aMin.x > bMax.x
    || aMax.y < bMin.y || aMin.y > bMax.y
    || aMax.z < bMin.z || aMin.z > bMax.z);
}

fn dynamicAwake(body: u32) -> bool {
  return positions[body].w > 0.0 && (bodyState[body] & BODY_SLEEPING) == 0u;
}

@compute @workgroup_size(${CANDIDATE_WORKGROUP_SIZE})
fn clearSparseState(@builtin(global_invocation_id) globalId: vec3u) {
  let gid = globalId.x;
  if (gid < uniforms.awakeBitsetWords) {
    atomicStore(&pairActivity[AWAKE_BITSET_OFFSET + gid], 0u);
  }
  if (gid == 0u) {
    atomicStore(&pairActivity[RETAINED_OFFSET], 0u);
    atomicStore(&pairActivity[AWAKE_OFFSET], 0u);
    atomicStore(&pairActivity[VALIDATOR_FAULT_OFFSET], 0u);
    atomicStore(&pairActivity[RETAINED_PREFIX_OFFSET], 0u);
    for (var i = 0u; i < ${SPARSE_ARGS_WORDS}u; i++) {
      atomicStore(&sparseArgs[i], 0u);
    }
    let rawHighWater = persistentManifoldMap[0u];
    if (rawHighWater > uniforms.maxPersistentManifolds) {
      latchValidatorFault(0x00000001u);
    }
    let highWater = min(rawHighWater, uniforms.maxPersistentManifolds);
    atomicStore(
      &sparseArgs[${SPARSE_ARGS_RETAIN_SCAN_OFFSET}u],
      (highWater + ${CANDIDATE_WORKGROUP_SIZE - 1}u) / ${CANDIDATE_WORKGROUP_SIZE}u,
    );
    atomicStore(&sparseArgs[${SPARSE_ARGS_RETAIN_SCAN_OFFSET + 1}u], 1u);
    atomicStore(&sparseArgs[${SPARSE_ARGS_RETAIN_SCAN_OFFSET + 2}u], 1u);
  }
}

@compute @workgroup_size(${CANDIDATE_WORKGROUP_SIZE})
fn retainSleepingPairs(@builtin(global_invocation_id) globalId: vec3u) {
  let manifold = globalId.x;
  let highWater = min(persistentManifoldMap[0u], uniforms.maxPersistentManifolds);
  if (manifold >= highWater) { return; }
  let packedPair = persistentManifoldMap[POOL_KEY_OFFSET + manifold];
  let outputMask = persistentManifoldMap[OUTPUT_MASK_OFFSET + manifold];
  if (packedPair == 0u || outputMask == 0u) { return; }
  if ((outputMask & ~VALID_OUTPUT_MASK) != 0u) {
    latchValidatorFault(0x00000002u);
    return;
  }
  let i = packedPair & 0xffffu;
  let j = packedPair >> 16u;
  if (i >= j || j >= uniforms.bodyCount) {
    latchValidatorFault(0x00000004u);
    return;
  }
  let invMassI = positions[i].w;
  let invMassJ = positions[j].w;
  if (!finiteScalar(invMassI) || !finiteScalar(invMassJ) || invMassI < 0.0 || invMassJ < 0.0) {
    latchValidatorFault(0x00000008u);
    return;
  }
  let dynamicI = invMassI > 0.0;
  let dynamicJ = invMassJ > 0.0;
  // Every tail body owns its prefix/tail candidate search until the immutable
  // BVH is rebuilt. Never seed the same tail pair from the retained pool.
  if (i >= uniforms.bvhBodyCount || j >= uniforms.bvhBodyCount) { return; }
  if (
    (bodyState[i] & BODY_INITIALIZED) == 0u
    || (bodyState[j] & BODY_INITIALIZED) == 0u
  ) {
    latchValidatorFault(0x00000010u);
    return;
  }
  if ((!dynamicI && !dynamicJ) || dynamicAwake(i) || dynamicAwake(j)) { return; }
  let minI = loadBodyMin(i);
  let maxI = loadBodyMax(i);
  let minJ = loadBodyMin(j);
  let maxJ = loadBodyMax(j);
  if (!finiteBounds(minI, maxI) || !finiteBounds(minJ, maxJ)) {
    latchValidatorFault(0x00000020u);
    return;
  }
  if (!overlaps(minI, maxI, minJ, maxJ)) {
    // Match the full broadphase: a stale/non-overlapping descriptor is not a
    // candidate this epoch and ages out through the ordinary compact reclaimer.
    // Treating that expected lifecycle as corruption would make an old
    // speculative row halt an otherwise healthy sleeping scene.
    return;
  }
  let writeIndex = atomicAdd(&pairActivity[RETAINED_OFFSET], 1u);
  if (writeIndex < uniforms.pairCapacity) {
    // Candidate prefix and pool-index sidecar share one queue-ordered write
    // index. Contact generation consumes the pair from the canonical prefix
    // and the descriptor directly from its otherwise-dead active-slot scratch.
    atomicStore(&pairActivity[writeIndex + 1u], packedPair);
    atomicStore(&pairActivity[RETAINED_OFFSET + writeIndex + 1u], manifold);
  } else {
    latchValidatorFault(0x00000040u);
  }
}

@compute @workgroup_size(${CANDIDATE_WORKGROUP_SIZE})
fn compactAwakeBodies(@builtin(global_invocation_id) globalId: vec3u) {
  let body = globalId.x;
  if (body >= uniforms.bodyCount) { return; }
  let invMass = positions[body].w;
  if (!finiteScalar(invMass) || invMass < 0.0) {
    latchValidatorFault(0x00000080u);
    return;
  }
  let tailBody = body >= uniforms.bvhBodyCount;
  if (invMass == 0.0 && !tailBody) { return; }
  let state = bodyState[body];
  let dynamicBody = invMass > 0.0;
  if (dynamicBody && (state & BODY_INITIALIZED) == 0u) {
    latchValidatorFault(0x00000100u);
    return;
  }
  let awakeDynamic = dynamicBody && (state & BODY_SLEEPING) == 0u;
  // Prefix ownership is awake-only. Every exact tail body remains a query
  // owner, including sleeping dynamics and statics absent from the old tree.
  if (!tailBody && !awakeDynamic) { return; }
  let writeIndex = atomicAdd(&pairActivity[AWAKE_OFFSET], 1u);
  if (writeIndex < ${this.maxBodies}u) {
    atomicStore(&pairActivity[AWAKE_OFFSET + writeIndex + 1u], body);
    if (awakeDynamic) {
      atomicOr(
        &pairActivity[AWAKE_BITSET_OFFSET + (body >> 5u)],
        1u << (body & 31u),
      );
    }
  } else {
    latchValidatorFault(0x00000200u);
  }
}

@compute @workgroup_size(1)
fn finalizeSparseState() {
  let faulted = atomicLoad(&pairActivity[VALIDATOR_FAULT_OFFSET]) != 0u;
  let retainedCount = min(atomicLoad(&pairActivity[RETAINED_OFFSET]), uniforms.pairCapacity);
  let awakeCount = min(atomicLoad(&pairActivity[AWAKE_OFFSET]), uniforms.bodyCount);
  atomicStore(
    &sparseArgs[${SPARSE_ARGS_RETAIN_SEED_OFFSET}u],
    select(
      (retainedCount + ${CANDIDATE_WORKGROUP_SIZE - 1}u) / ${CANDIDATE_WORKGROUP_SIZE}u,
      0u,
      faulted,
    ),
  );
  atomicStore(&sparseArgs[${SPARSE_ARGS_RETAIN_SEED_OFFSET + 1}u], 1u);
  atomicStore(&sparseArgs[${SPARSE_ARGS_RETAIN_SEED_OFFSET + 2}u], 1u);
  let traversalBodies = select(awakeCount, uniforms.bodyCount, faulted);
  atomicStore(
    &sparseArgs[${SPARSE_ARGS_AWAKE_TRAVERSAL_OFFSET}u],
    (traversalBodies + ${CANDIDATE_WORKGROUP_SIZE - 1}u) / ${CANDIDATE_WORKGROUP_SIZE}u,
  );
  atomicStore(&sparseArgs[${SPARSE_ARGS_AWAKE_TRAVERSAL_OFFSET + 1}u], 1u);
  atomicStore(&sparseArgs[${SPARSE_ARGS_AWAKE_TRAVERSAL_OFFSET + 2}u], 1u);
}
`,
    });
    const sparsePipelineLayout = this.device.createPipelineLayout({
      bindGroupLayouts: [this.sparseSleepingBindGroupLayout],
    });
    const sparseWorkPipelineLayout = this.device.createPipelineLayout({
      bindGroupLayouts: [this.sparseSleepingWorkBindGroupLayout],
    });
    const createSparsePipeline = (
      label: string,
      entryPoint: string,
      workOnly = false,
    ): Promise<GPUComputePipeline> => (
      this.device.createComputePipelineAsync({
        label,
        layout: workOnly ? sparseWorkPipelineLayout : sparsePipelineLayout,
        compute: { module: sparseModule, entryPoint },
      })
    );
    this.sparsePipelineState = 'pending';
    this.sparsePipelinePrewarm = Promise.resolve().then(async () => {
      const results = await Promise.allSettled([
        createSparsePipeline(
          'Broadphase Sparse Sleeping Clear',
          'clearSparseState',
        ),
        createSparsePipeline(
          'Broadphase Sparse Sleeping Retain Manifolds',
          'retainSleepingPairs',
          true,
        ),
        createSparsePipeline(
          'Broadphase Sparse Sleeping Compact Awake Bodies',
          'compactAwakeBodies',
          true,
        ),
        createSparsePipeline(
          'Broadphase Sparse Sleeping Finalize Indirect Args',
          'finalizeSparseState',
        ),
      ]);
      // Sparse compilation may settle after dispose(); in that case none of
      // its pipelines or lifecycle state belongs to the dead instance.
      if (this.disposed) return;
      const rejected = results.find(
        (result): result is PromiseRejectedResult => result.status === 'rejected',
      );
      if (rejected) throw rejected.reason;
      [
        this.sparseClearPipeline,
        this.sparseRetainPipeline,
        this.sparseCompactAwakePipeline,
        this.sparseFinalizePipeline,
      ] = results.map((result) => (
        (result as PromiseFulfilledResult<GPUComputePipeline>).value
      ));
      this.sparsePipelineState = 'ready';
    }).catch((error) => {
      if (this.disposed) return;
      this.sparsePipelineError = this.makeStartupPipelineError(
        'BroadPhaseStage sparse sleeping pipeline prewarm failed',
        error,
      );
      this.sparsePipelineState = 'error';
      console.error(this.sparsePipelineError);
    });
    this.appendStartupPrewarmFence(this.sparsePipelinePrewarm);
  }

  setForceFullSleepingSparseForTesting(forceFull: boolean): void {
    this.forceFullSparseSleepingForTesting = forceFull;
  }

  /** Regression-only opt-in while the production gate remains closed. */
  setSleepingSparseModeForTesting(enabled: boolean, minimumBodyCount = 1): void {
    this.sparseSleepingEnabled = enabled;
    this.sparseSleepingMinimumBodyCount = Math.max(1, Math.floor(minimumBodyCount));
    if (!enabled) this.sparseSelectedLast = false;
  }

  getSleepingSparseDiagnostics(): BroadphaseSleepingSparseDiagnostics {
    return Object.freeze({
      attached: this.sparseSleepingInputs !== null,
      enabled: this.sparseSleepingEnabled,
      minimumBodyCount: this.sparseSleepingMinimumBodyCount,
      selectedLast: this.sparseSelectedLast,
      selectedDispatches: this.sparseSelectedDispatches,
      certificateGenerationLast: this.sparseCertificateGenerationLast,
      forcedFullForTesting: this.forceFullSparseSleepingForTesting,
    });
  }

  /** Regression-only readback of the compact sparse-preparation scratch header. */
  async readSleepingSparseScratchDiagnosticsAsync(
    renderer: any,
  ): Promise<BroadphaseSleepingSparseScratchDiagnostics | null> {
    const inputs = this.sparseSleepingInputs;
    if (!inputs || !renderer || typeof renderer.getArrayBufferAsync !== 'function') {
      return null;
    }
    // Three's WebGPU readback path owns mutable backend command state. Keep
    // diagnostic maps strictly ordered even though they target two buffers.
    const raw = await renderer.getArrayBufferAsync(this.pairActivityAttr);
    const bodyStateRaw = await renderer.getArrayBufferAsync(inputs.bodyState);
    const words = new Uint32Array(raw);
    const bodyStateWords = new Uint32Array(bodyStateRaw);
    const awakeBitsetWords = Math.max(1, Math.ceil(this.maxBodies / 32));
    const validatorOffset = inputs.awakeBodyScratchOffset
      + 1 + this.maxBodies + awakeBitsetWords;
    return Object.freeze({
      retainedCount: words[inputs.retainedPairScratchOffset] ?? 0,
      awakeCount: words[inputs.awakeBodyScratchOffset] ?? 0,
      validatorFaultBits: words[validatorOffset] ?? 0,
      retainedPrefixScratch: words[validatorOffset + 1] ?? 0,
      bodyStatePrefix: Object.freeze(
        Array.from(bodyStateWords.slice(0, Math.min(16, this.maxBodies))),
      ),
    });
  }

  private prepareSparseSleepingDispatch(
    renderer: any,
    bodyCount: number,
    pairCapacity: number,
    aabbPositionBuffer: GPUBuffer,
    pairActivityBuffer: GPUBuffer,
    positionsBuffer: GPUBuffer,
    safetyGateBuffer: GPUBuffer,
    useVisitedDedup: boolean,
  ): boolean {
    const inputs = this.sparseSleepingInputs;
    this.sparseSelectedLast = false;
    if (
      !inputs
      // Retained sleeping pairs are still sourced from the legacy packed
      // persistent-map key. Until that producer is widened, selecting sparse
      // traversal here would seed truncated endpoints into an otherwise exact
      // wide candidate stream. Dense traversal remains complete.
      || this.pairEndpointEncoding === 'wide-u32'
      || !this.sparseSleepingEnabled
      || this.forceFullSparseSleepingForTesting
      || bodyCount < this.sparseSleepingMinimumBodyCount
      || this.activeBvhBodyCount <= 0
      || this.activeBvhBodyCount > bodyCount
      || bodyCount - this.activeBvhBodyCount > MAX_EXACT_DELTA_BODIES
      || useVisitedDedup
      || !this.sparseSleepingUniformBuffer
      || !this.sparseSleepingArgsBuffer
      || !this.sparseSleepingBindGroupLayout
      || !this.sparseSleepingWorkBindGroupLayout
    ) {
      return false;
    }
    let certificate: SleepingSparseBroadphaseCertificate;
    try {
      certificate = inputs.getCertificate();
    } catch (error) {
      console.warn('Broadphase sparse sleeping certificate provider failed open:', error);
      return false;
    }
    this.sparseCertificateGenerationLast = certificate.generation;
    if (!certificate.eligible || certificate.confirmedBodyCount !== bodyCount) {
      return false;
    }
    const backend = renderer?.backend;
    const bodyStateBuffer = backend?.get?.(inputs.bodyState)?.buffer as GPUBuffer | undefined;
    const persistentMapBuffer = backend?.get?.(inputs.persistentManifoldMap)?.buffer as
      | GPUBuffer
      | undefined;
    if (!bodyStateBuffer || !persistentMapBuffer) return false;
    const buffers = Object.freeze([
      aabbPositionBuffer,
      pairActivityBuffer,
      bodyStateBuffer,
      positionsBuffer,
      persistentMapBuffer,
      safetyGateBuffer,
    ] as const);
    const buffersChanged = !this.sparseSleepingBindGroupBuffers
      || buffers.some((buffer, index) => this.sparseSleepingBindGroupBuffers![index] !== buffer);
    if (buffersChanged || !this.sparseSleepingBindGroup || !this.sparseSleepingWorkBindGroup) {
      const commonEntries: GPUBindGroupEntry[] = [
        { binding: 0, resource: { buffer: this.sparseSleepingUniformBuffer } },
        { binding: 1, resource: { buffer: aabbPositionBuffer } },
        { binding: 2, resource: { buffer: pairActivityBuffer } },
        { binding: 3, resource: { buffer: bodyStateBuffer } },
        { binding: 4, resource: { buffer: positionsBuffer } },
        { binding: 5, resource: { buffer: persistentMapBuffer } },
        { binding: 7, resource: { buffer: safetyGateBuffer } },
      ];
      this.sparseSleepingBindGroup = this.device.createBindGroup({
        label: 'Broadphase Sparse Sleeping BindGroup',
        layout: this.sparseSleepingBindGroupLayout,
        entries: [
          ...commonEntries,
          { binding: 6, resource: { buffer: this.sparseSleepingArgsBuffer } },
        ],
      });
      this.sparseSleepingWorkBindGroup = this.device.createBindGroup({
        label: 'Broadphase Sparse Sleeping Work BindGroup',
        layout: this.sparseSleepingWorkBindGroupLayout,
        entries: commonEntries,
      });
      this.sparseSleepingBindGroupBuffers = buffers;
    }
    this.device.queue.writeBuffer(
      this.sparseSleepingUniformBuffer,
      0,
      new Uint32Array([
        bodyCount,
        pairCapacity,
        inputs.maxPersistentManifolds,
        Math.max(1, Math.ceil(this.maxBodies / 32)),
        this.activeBvhBodyCount,
      ]),
    );
    this.sparseSelectedLast = true;
    return true;
  }

  private selectSleepingAabbReuse(bodyCount: number): boolean {
    this.sleepingAabbReuseSelectedLast = false;
    const provider = this.sleepingAabbCertificateProvider;
    if (!provider || bodyCount <= 0 || this.fatInitializedBodyCount < bodyCount) return false;
    try {
      const certificate = provider();
      const selected = certificate.eligible && certificate.confirmedBodyCount === bodyCount;
      this.sleepingAabbReuseSelectedLast = selected;
      if (selected) this.sleepingAabbReuseSelections++;
      return selected;
    } catch (error) {
      if (!this.sleepingAabbCertificateFailureWarned) {
        console.warn('Broadphase sleeping AABB certificate provider failed open:', error);
        this.sleepingAabbCertificateFailureWarned = true;
      }
      return false;
    }
  }

  private recordSparseSleepingPreparation(
    commandEncoder: GPUCommandEncoder,
    bodyCount: number,
  ): boolean {
    if (
      !this.sparseSelectedLast
      || !this.sparseSleepingBindGroup
      || !this.sparseSleepingWorkBindGroup
      || !this.sparseSleepingArgsBuffer
      || !this.sparseClearPipeline
      || !this.sparseRetainPipeline
      || !this.sparseCompactAwakePipeline
      || !this.sparseFinalizePipeline
    ) return false;
    const clearPass = commandEncoder.beginComputePass(
      this.createComputePassDescriptor('Broadphase Sparse Sleeping Clear + Plan'),
    );
    clearPass.setPipeline(this.sparseClearPipeline);
    clearPass.setBindGroup(0, this.sparseSleepingBindGroup);
    clearPass.dispatchWorkgroups(Math.max(1, Math.ceil(this.maxBodies / 32 / CANDIDATE_WORKGROUP_SIZE)));
    clearPass.end();

    const workPass = commandEncoder.beginComputePass(
      this.createComputePassDescriptor('Broadphase Sparse Sleeping Retain + Compact'),
    );
    workPass.setBindGroup(0, this.sparseSleepingWorkBindGroup);
    workPass.setPipeline(this.sparseRetainPipeline);
    workPass.dispatchWorkgroupsIndirect(
      this.sparseSleepingArgsBuffer,
      SPARSE_ARGS_RETAIN_SCAN_OFFSET * 4,
    );
    workPass.setPipeline(this.sparseCompactAwakePipeline);
    workPass.dispatchWorkgroups(Math.max(1, Math.ceil(bodyCount / CANDIDATE_WORKGROUP_SIZE)));
    workPass.end();

    const finalizePass = commandEncoder.beginComputePass(
      this.createComputePassDescriptor('Broadphase Sparse Sleeping Finalize'),
    );
    finalizePass.setPipeline(this.sparseFinalizePipeline);
    finalizePass.setBindGroup(0, this.sparseSleepingBindGroup);
    finalizePass.dispatchWorkgroups(1);
    finalizePass.end();
    this.sparseSelectedDispatches++;
    return true;
  }

  /**
   * Records the already-compiled Three TSL AABB kernel into a caller-owned
   * encoder. Three remains responsible for shader generation, uniforms, and
   * storage bindings; only command recording/submission is moved here. This
   * preserves the exact swept/fat/angular-padding shader rather than keeping a
   * second hand-translated WGSL implementation in sync.
   */
  private recordUpdateAabbIntoEncoder(
    renderer: any,
    commandEncoder: GPUCommandEncoder,
    bodyCount: number,
  ): boolean {
    if (
      !this.updateAabbNativeReady
      || this.updateAabbNativeRenderer !== renderer
      || bodyCount <= 0
    ) {
      return false;
    }

    let prepared: PreparedThreeWebGpuCompute | null = null;
    try {
      const result = prepareThreeWebGpuCompute(
        renderer,
        [{
          node: this.updateAabbKernel,
          passIndex: 0,
          // This is already a workgroup count, so use the exact [x,y,z] form.
          dispatchSize: [Math.ceil(bodyCount / WORKGROUP_SIZE), 1, 1],
        }],
        {
          accountRendererCall: false,
          preparationCache: this.updateAabbPreparationCache,
        },
      );
      if (!result.ok) {
        if (this.updateAabbNativeFallbacks <= 1) {
          console.warn(
            'BroadPhaseStage Three r183 AABB bridge is unavailable:',
            result.reason,
          );
        }
        return false;
      }
      prepared = result;
      recordPreparedThreeWebGpuCompute(
        commandEncoder,
        prepared,
        'Broadphase Update AABBs',
        {
          computePassDescriptorFactory: this.nativeComputePassDescriptorFactory,
        },
      );
      return true;
    } catch (error) {
      prepared?.rollbackAccounting();
      if (this.updateAabbNativeFallbacks <= 1) {
        console.warn('BroadPhaseStage could not record the TSL AABB kernel natively:', error);
      }
      return false;
    }
  }

  private submitStandaloneAabb(renderer: any, bodyCount: number, frameId: number): void {
    // Only the first dispatch for a renderer is initialization. If Three's
    // private native-recording surface is unavailable (or later changes),
    // every subsequent standalone dispatch is real steady-state work and
    // must remain visible as an extra critical submit in the budget ledger.
    const firstNativeCompile = this.updateAabbNativeRenderer !== renderer;
    const submissionClass = firstNativeCompile ? 'init' : 'critical';
    const submissionLabel = firstNativeCompile
      ? 'broadphase-aabb-native-prime'
      : 'broadphase-aabb-fallback';
    const submit = (): void => renderer.compute(
      this.updateAabbKernel,
      [Math.ceil(bodyCount / WORKGROUP_SIZE), 1, 1],
    );
    if (this.submissionLedger) {
      this.submissionLedger.withActualSubmitScope(
        this.device.queue,
        {
          frameId,
          submissionClass,
          label: submissionLabel,
          fixedStepId: this.currentFixedStepId,
          fixedStepSpan: this.currentFixedStepSpan,
        },
        submit,
      );
    } else {
      submit();
    }
    this.submissionLedger?.record(
      submissionClass,
      submissionLabel,
      {
        frameId,
        fixedStepId: this.currentFixedStepId,
        fixedStepSpan: this.currentFixedStepSpan,
      },
    );
    this.updateAabbNativeRenderer = renderer;
    this.updateAabbNativeReady = isThreeWebGpuComputeBridgeReady(
      renderer,
      [this.updateAabbKernel],
    );
  }

  dispatch(
    renderer: any,
    bodyCount: number,
    pairCount: number,
    frameId: number,
    fixedTimeStep = 1.0 / 60.0,
    fixedStepId: number | null = null,
    fixedStepSpan = 1,
    computeTail?: BroadphaseComputeTail,
  ): void {
    if (this.disposed) { return; }
    if (computeTail) {
      computeTail.fused = false;
      computeTail.broadphaseSubmitted = false;
      computeTail.fallbackReason = null;
    }
    this.currentFixedStepId = fixedStepId;
    this.currentFixedStepSpan = Math.max(1, Math.floor(fixedStepSpan));
    this.requestedBodyCount = bodyCount;
    this.lastDispatchFrame = frameId;
    const activeBvhMatchesBodyCount =
      this.hasBuiltOnce && this.activeBvhBodyCount === bodyCount;
    if (!activeBvhMatchesBodyCount && !this.hasBuiltOnce) {
      // Before the first tree is available there is nothing safe to traverse.
      // Once a tree exists, the candidate shader covers appended bodies with
      // an exact delta path while the compatible replacement builds.
      this.candidatePairsEnabled = false;
      this.bvhBuffersReady = false;
    }

    if (bodyCount < 2 || pairCount == 0) {
      this.candidatePairsEnabled = false;
      this.backendBuffersReady = false;
      this.bvhBuffersReady = false;
      this.lastBuildMs = 0;
      this.logDebugState(frameId, bodyCount, 'insufficientBodiesOrPairs');
      return;
    }

    let startupPipelineError = this.getStartupPipelineError();
    const candidatePipelines = this.getCandidatePipelineSet();
    if (!startupPipelineError && this.candidatePipelineState === 'ready' && !candidatePipelines) {
      startupPipelineError = this.makeStartupPipelineError(
        'BroadPhaseStage candidate pipeline prewarm failed closed',
        'ready state has an incomplete pipeline set',
      );
      this.candidatePipelineError = startupPipelineError;
      this.candidatePipelineState = 'error';
      console.error(startupPipelineError);
    }
    if (startupPipelineError || !this.isStartupPipelineReady() || !candidatePipelines) {
      this.candidatePairsEnabled = false;
      this.backendBuffersReady = false;
      this.bvhBuffersReady = false;
      if (computeTail) {
        computeTail.fallbackReason = startupPipelineError
          ? startupPipelineError.message
          : `broadphase startup prewarm pending: ${this.getStartupPipelineWaitReason()}`;
      }
      this.logDebugState(
        frameId,
        bodyCount,
        startupPipelineError ? 'startupPipelineError' : 'waitingPipelinePrewarm',
      );
      return;
    }

    const backend = (renderer as any).backend;
    if (!this.storageBuffersInitialized && backend?.createStorageAttribute) {
      try {
        const ignoredPairHashWasMaterialized = Boolean(
          backend?.get?.(this.ignoredPairHashAttr)?.buffer,
        );
        const safetyGateWasMaterialized = Boolean(
          backend?.get?.(this.safetyGateAttr)?.buffer,
        );
        // Candidate traversal consumes these buffers through native WebGPU
        // bindings, so materialize them before the first TSL AABB dispatch.
        backend.createStorageAttribute(this.positionsAttr);
        backend.createStorageAttribute(this.shapesAttr);
        backend.createStorageAttribute(this.aabbPositionAttr);
        backend.createStorageAttribute(this.dirtyLeafAttr);
        backend.createStorageAttribute(this.aabbSleepingBodyStateAttr);
        backend.createStorageAttribute(this.pairActivityAttr);
        backend.createStorageAttribute(this.pairVisitedBitsAttr);
        backend.createStorageAttribute(this.ignoredPairHashAttr);
        backend.createStorageAttribute(this.safetyGateAttr);
        // createStorageAttribute is a no-op for an existing backend buffer.
        // After reset(), explicitly publish the new CPU-side zero/hash values
        // before native candidate generation reads them; otherwise an old
        // scene's sticky fatal or ignored-pair table survives the first step.
        if (ignoredPairHashWasMaterialized) {
          backend.updateAttribute(this.ignoredPairHashAttr);
        }
        if (safetyGateWasMaterialized) {
          backend.updateAttribute(this.safetyGateAttr);
        }
        if (
          !acknowledgeRendererAttributeVersion(renderer, this.ignoredPairHashAttr)
          || !acknowledgeRendererAttributeVersion(renderer, this.safetyGateAttr)
        ) {
          throw new Error('renderer attribute cache is unavailable for native broadphase storage');
        }
        this.ignoredPairHashUploadedVersion = this.ignoredPairHashAttr.version;
        this.safetyGateUploadedVersion = this.safetyGateAttr.version;
        this.storageBuffersInitialized = true;
      } catch (error) {
        if (!this.storageInitWarned) {
          console.warn('BroadPhaseStage failed to initialize storage attributes:', error);
          this.storageInitWarned = true;
        }
      }
    }

    const positionsBuffer = backend?.get?.(this.positionsAttr)?.buffer as GPUBuffer | undefined;
    const shapesBuffer = backend?.get?.(this.shapesAttr)?.buffer as GPUBuffer | undefined;
    const aabbPositionBuffer = backend?.get?.(this.aabbPositionAttr)?.buffer as GPUBuffer | undefined;
    const dirtyLeafBuffer = backend?.get?.(this.dirtyLeafAttr)?.buffer as GPUBuffer | undefined;
    const pairActivityBuffer = backend?.get?.(this.pairActivityAttr)?.buffer as GPUBuffer | undefined;
    const pairVisitedBuffer = backend?.get?.(this.pairVisitedBitsAttr)?.buffer as GPUBuffer | undefined;
    const ignoredPairHashBuffer = backend?.get?.(this.ignoredPairHashAttr)?.buffer as GPUBuffer | undefined;
    const safetyGateBuffer = backend?.get?.(this.safetyGateAttr)?.buffer as GPUBuffer | undefined;
    this.backendBuffersReady = Boolean(
      this.storageBuffersInitialized
      && positionsBuffer
      && shapesBuffer
      && aabbPositionBuffer
      && dirtyLeafBuffer
      && pairActivityBuffer
      && pairVisitedBuffer
      && ignoredPairHashBuffer
      && safetyGateBuffer
    );
    if (
      !positionsBuffer
      || !shapesBuffer
      || !aabbPositionBuffer
      || !dirtyLeafBuffer
      || !pairActivityBuffer
      || !pairVisitedBuffer
      || !ignoredPairHashBuffer
      || !safetyGateBuffer
    ) {
      this.candidatePairsEnabled = false;
      this.bvhBuffersReady = false;
      this.logDebugState(frameId, bodyCount, 'missingBackendBuffers');
      return;
    }

    this.latestAabbPositionBuffer = aabbPositionBuffer;

    // During initial pipeline prewarm/build there is no published tree that
    // can consume another AABB update. The first ready dispatch already queues
    // AABB -> immutable snapshot -> build in order; later animation frames must
    // wait without resubmitting an unused O(N) AABB pass. If body count changes
    // while the immutable build is in flight, its stale result is discarded and
    // the first dispatch after completion snapshots the new generation.
    if (!this.hasBuiltOnce && (this.prewarmInFlight !== null || this.buildInFlight)) {
      this.candidatePairsEnabled = false;
      this.bvhBuffersReady = false;
      this.logDebugState(
        frameId,
        bodyCount,
        this.prewarmInFlight !== null ? 'waitingPrewarm' : 'waitingInitialBuild',
      );
      return;
    }

    this.updateAabbKernel.computeNode.parameters.bodyCount.value = bodyCount;
    this.updateAabbKernel.computeNode.parameters.aabbFrameId.value = frameId >>> 0;
    if (bodyCount < this.fatInitializedBodyCount) {
      this.fatInitializedBodyCount = bodyCount;
    }
    this.updateAabbKernel.computeNode.parameters.initializedBodyCount.value = this.fatInitializedBodyCount;
    this.updateAabbKernel.computeNode.parameters.activeTreeBodyCount.value = this.hasBuiltOnce
      ? Math.max(0, Math.min(bodyCount, this.activeBvhBodyCount))
      : 0;
    this.updateAabbKernel.computeNode.parameters.reuseSleepingAabbs.value =
      this.selectSleepingAabbReuse(bodyCount) ? 1 : 0;
    const boundedFixedTimeStep = Math.max(1e-6, fixedTimeStep);
    this.updateAabbKernel.computeNode.parameters.aabbQueryHorizon.value = boundedFixedTimeStep;
    this.updateAabbKernel.computeNode.parameters.aabbFatHorizon.value = boundedFixedTimeStep * 2.0;

    // Reset the compact-list header before the sole TSL AABB dispatch. A queue
    // write is ordered before subsequent queue work and avoids a second Three
    // compute submit solely to clear eight words.
    this.dirtyHeader.fill(0);
    this.dirtyHeader[2] = bodyCount;
    this.device.queue.writeBuffer(dirtyLeafBuffer, 0, this.dirtyHeader);
    const earlyActiveBVH = this.getActiveBVH();
    const appendedToActiveTree = this.hasBuiltOnce
      ? Math.max(0, bodyCount - this.activeBvhBodyCount)
      : Number.POSITIVE_INFINITY;
    const mergeTopologyEligible = this.enableBvhBuild
      && this.hasBuiltOnce
      && bodyCount >= this.activeBvhBodyCount
      && appendedToActiveTree <= MAX_EXACT_DELTA_BODIES
      && Boolean(earlyActiveBVH.bvh2Buffer && earlyActiveBVH.parentIdxBuffer);
    const canMergeAabbWithTraversal = mergeTopologyEligible
      && this.updateAabbNativeReady
      && this.updateAabbNativeRenderer === renderer;
    if (!canMergeAabbWithTraversal) {
      if (mergeTopologyEligible && this.updateAabbNativeRenderer === renderer) {
        this.updateAabbNativeFallbacks++;
      }
      this.submitStandaloneAabb(renderer, bodyCount, frameId);
    }
    this.fatInitializedBodyCount = bodyCount;

    if (!this.bootstrapDone) {
      const submitBootstrap = (): void => renderer.compute(this.bootstrapKernel, [1, 1, 1]);
      if (this.submissionLedger) {
        this.submissionLedger.withActualSubmitScope(
          this.device.queue,
          {
            frameId,
            submissionClass: 'init',
            label: 'broadphase-storage-bootstrap',
          },
          submitBootstrap,
        );
      } else {
        submitBootstrap();
      }
      this.submissionLedger?.record('init', 'broadphase-storage-bootstrap', { frameId });
      this.bootstrapDone = true;
    }

    if (!this.enableBvhBuild) {
      this.candidatePairsEnabled = false;
      this.bvhBuffersReady = false;
      this.lastBuildMs = 0;
      this.logDebugState(frameId, bodyCount, 'bvhDisabled');
      return;
    }

    // A refit keeps bounds conservative without changing Morton topology.
    // rebuildIntervalFrames is now only a quality-check/rebuild cooldown; it
    // must not itself force a periodic 64k sort/topology rebuild.
    const rebuildIntervalScale = Math.min(8, Math.max(1, Math.ceil(bodyCount / 8192)));
    const adaptiveRebuildInterval = this.rebuildIntervalFrames * rebuildIntervalScale;
    const rebuildCooldownReady = this.lastBuildFrame < 0
      || frameId - this.lastBuildFrame >= adaptiveRebuildInterval;
    const qualitySampleDue = this.qualitySampleCooldownFrame < 0
      || frameId - this.qualitySampleCooldownFrame >= adaptiveRebuildInterval;
    const prewarmReady = this.prewarmInFlight === null && this.isStartupPipelineReady();
    const needsCompatibleBuild = !activeBvhMatchesBodyCount;
    const appendedBodyCount = this.hasBuiltOnce
      ? Math.max(0, bodyCount - this.activeBvhBodyCount)
      : 0;
    const exactTailRebuildThreshold = this.hasBuiltOnce
      ? Math.max(64, Math.min(512, Math.ceil(this.activeBvhBodyCount / 256)))
      : 64;
    const bodyCountShrank = this.hasBuiltOnce && bodyCount < this.activeBvhBodyCount;
    const exactTailLimitExceeded = appendedBodyCount > MAX_EXACT_DELTA_BODIES;
    const tailRebuildReady = appendedBodyCount >= exactTailRebuildThreshold;
    // An appended tail is represented exactly by the candidate shader. Do not
    // turn a one-body projectile into a full 64k sort+topology rebuild. A
    // compatibility build is needed only when the exact tail becomes material,
    // exceeds its bounded fallback, or quality diagnostics request a refresh.
    const compatibilityBuildRequired = needsCompatibleBuild && (
      !this.hasBuiltOnce
      || bodyCountShrank
      || exactTailLimitExceeded
      || tailRebuildReady
      || this.topologyQualityRebuildRequested
    );
    const qualityBuildRequired = !needsCompatibleBuild
      && rebuildCooldownReady
      && this.topologyQualityRebuildRequested;
    const shouldStartBuild = !this.buildInFlight
      && prewarmReady
      && (compatibilityBuildRequired || qualityBuildRequired);
    // Record the actual trigger, not merely the current body-count mismatch.
    // A small appended tail rebuilt only because quality degraded remains a
    // quality build; a threshold/limit-driven append remains a body-grow.
    const scheduledBuildReason: BroadphaseBuildReason = !this.hasBuiltOnce
      ? 'initial'
      : bodyCountShrank
        ? 'body-shrink'
        : needsCompatibleBuild && (exactTailLimitExceeded || tailRebuildReady)
          ? 'body-grow'
          : this.pendingRebuildReason
            ?? (needsCompatibleBuild && bodyCount > this.activeBvhBodyCount ? 'body-grow' : 'manual');

    if (needsCompatibleBuild && !this.hasBuiltOnce) {
      if (shouldStartBuild) {
        this.startBuild(aabbPositionBuffer, bodyCount, frameId, scheduledBuildReason);
      }
      this.candidatePairsEnabled = false;
      this.bvhBuffersReady = false;
      const waitReason = !prewarmReady
        ? 'waitingPrewarm'
        : this.buildInFlightBodyCount >= 0 && this.buildInFlightBodyCount !== bodyCount
          ? 'waitingStaleBodyCountBuild'
          : this.hasBuiltOnce
            ? 'waitingBodyCountRebuild'
            : 'waitingInitialBuild';
      this.logDebugState(frameId, bodyCount, waitReason, shouldStartBuild);
      return;
    }

    if (needsCompatibleBuild && this.hasBuiltOnce) {
      if (bodyCountShrank) {
        if (shouldStartBuild) {
          this.startBuild(aabbPositionBuffer, bodyCount, frameId, scheduledBuildReason);
        }
        // Append-only tails are exact; removals are not. Never traverse a
        // topology whose leaves refer to a larger body generation.
        this.candidatePairsEnabled = false;
        this.bvhBuffersReady = false;
        this.logDebugState(frameId, bodyCount, 'waitingShrunkBodyCountBuild', shouldStartBuild);
        return;
      }
      if (exactTailLimitExceeded) {
        if (shouldStartBuild) {
          this.startBuild(aabbPositionBuffer, bodyCount, frameId, scheduledBuildReason);
        }
        // The exact appended-tail fallback is quadratic in delta. A very large
        // runtime batch waits for one compatible tree generation instead of
        // issuing an unbounded GPU loop or silently omitting pairs.
        this.candidatePairsEnabled = false;
        this.bvhBuffersReady = false;
        this.logDebugState(frameId, bodyCount, 'waitingLargeDeltaBuild', shouldStartBuild);
        return;
      }
    }

    const activeBVH = this.getActiveBVH();
    const bvhBuffer = activeBVH.bvh2Buffer;
    const parentIdxBuffer = activeBVH.parentIdxBuffer;
    this.bvhBuffersReady = Boolean(bvhBuffer && parentIdxBuffer);
    if (!bvhBuffer || !parentIdxBuffer) {
      this.candidatePairsEnabled = false;
      if (shouldStartBuild) {
        this.startBuild(aabbPositionBuffer, bodyCount, frameId, scheduledBuildReason);
      }
      this.logDebugState(frameId, bodyCount, 'missingBvhBuffers', shouldStartBuild);
      return;
    }

    if (
      this.storageBuffersInitialized
      && this.ignoredPairHashUploadedVersion !== this.ignoredPairHashAttr.version
      && typeof backend?.updateAttribute === 'function'
    ) {
      backend.updateAttribute(this.ignoredPairHashAttr);
      if (!acknowledgeRendererAttributeVersion(renderer, this.ignoredPairHashAttr)) {
        this.candidatePairsEnabled = false;
        this.backendBuffersReady = false;
        this.bvhBuffersReady = false;
        this.logDebugState(frameId, bodyCount, 'rendererAttributeCacheUnavailable');
        return;
      }
      this.ignoredPairHashUploadedVersion = this.ignoredPairHashAttr.version;
    }
    if (
      this.storageBuffersInitialized
      && this.safetyGateUploadedVersion !== this.safetyGateAttr.version
      && typeof backend?.updateAttribute === 'function'
    ) {
      // Scene reset is a CPU-authored clear. Upload it before the raw candidate
      // encoder writes either gate bit or counter, and acknowledge the version
      // so a later renderer.compute cannot overwrite same-substep GPU results.
      backend.updateAttribute(this.safetyGateAttr);
      if (!acknowledgeRendererAttributeVersion(renderer, this.safetyGateAttr)) {
        this.candidatePairsEnabled = false;
        this.backendBuffersReady = false;
        this.bvhBuffersReady = false;
        this.logDebugState(frameId, bodyCount, 'rendererAttributeCacheUnavailable');
        return;
      }
      this.safetyGateUploadedVersion = this.safetyGateAttr.version;
    }

    const visitedWordCount = Math.ceil(pairCount / 32);
    const maxWorkgroupsPerDim = Math.max(1, Number(this.device.limits.maxComputeWorkgroupsPerDimension ?? 65535));
    const maxVisitedWordsPerDispatch = maxWorkgroupsPerDim * CANDIDATE_WORKGROUP_SIZE;
    const visitedWordCapacity = this.pairVisitedBitsAttr.count;
    const maxVisitedWordsUsable = Math.min(maxVisitedWordsPerDispatch, visitedWordCapacity);
    // The optional dense triangular bitset is indexed by a single u32 pair
    // ordinal and is not an exact identity structure above 16-bit endpoint
    // worlds. The traversal ownership rules remain complete without it.
    const useVisitedDedup = this.pairEndpointEncoding === 'packed-u16'
      && this.pairVisitedDedupConfigured
      && visitedWordCount <= maxVisitedWordsUsable
      ? 1
      : 0;
    const clearVisitedWorkgroups = useVisitedDedup
      ? Math.max(1, Math.ceil(visitedWordCount / CANDIDATE_WORKGROUP_SIZE))
      : 0;
    this.pairVisitedRequiredWordsLast = visitedWordCount;
    this.pairVisitedDedupEffectiveLast = useVisitedDedup !== 0;
    this.pairVisitedClearWorkgroupsLast = clearVisitedWorkgroups;
    this.pairVisitedFrameLast = frameId;

    const pairCapacity = Math.min(this.maxPairs, bodyCount * this.maxPairsPerBody);
    let sparseSleepingMode = this.prepareSparseSleepingDispatch(
      renderer,
      bodyCount,
      pairCapacity,
      aabbPositionBuffer,
      pairActivityBuffer,
      positionsBuffer,
      safetyGateBuffer,
      useVisitedDedup !== 0,
    );
    const sparseInputs = this.sparseSleepingInputs;
    const sparseAwakeBitsetOffset = sparseInputs
      ? sparseInputs.awakeBodyScratchOffset + 1 + this.maxBodies
      : 0;
    // Avoid WGSL arrayLength() on storage buffers here. Recent Chromium/Dawn
    // builds can lower that through immediate data paths that are not enabled
    // in standard WebGPU configurations.
    const activeBvhBodyCount = Math.max(1, Math.min(bodyCount, this.activeBvhBodyCount));
    const bvhNodeCapacity = Math.max(1, activeBvhBodyCount * 2);
    const measuredTreeDepth = activeBVH.refitTreeDepth ?? null;
    // The private-stack walk visits each node once and is substantially faster
    // on the production trees. Keep the parent-walk fallback for a newly
    // published tree whose asynchronous depth sample is not ready yet, or for
    // an unusually deep but otherwise valid Morton topology.
    const useStacklessTraversal = measuredTreeDepth === null
      || measuredTreeDepth + 2 > PAIR_STACK_SIZE
      ? 1
      : 0;
    const uniforms = new Uint32Array([
      bodyCount,
      pairCapacity,
      visitedWordCount,
      this.maxPairsPerBody,
      bvhNodeCapacity,
      useVisitedDedup,
      this.debugEnabled ? 1 : 0,
      this.ignoredPairHashCapacity,
      activeBvhBodyCount,
      useStacklessTraversal,
      this.forceRefitFatalForTesting ? 1 : 0,
      sparseSleepingMode ? 1 : 0,
      sparseSleepingMode && sparseInputs ? sparseInputs.retainedPairScratchOffset : 0,
      sparseSleepingMode && sparseInputs ? sparseInputs.awakeBodyScratchOffset : 0,
      sparseSleepingMode ? sparseAwakeBitsetOffset : 0,
    ]);
    this.device.queue.writeBuffer(this.candidateUniformBuffer, 0, uniforms);

    const needsBindGroupRebuild =
      !this.candidateBindGroup
      || this.candidateBindGroupBuffers.positions !== positionsBuffer
      || this.candidateBindGroupBuffers.aabbPosition !== aabbPositionBuffer
      || this.candidateBindGroupBuffers.bvh !== bvhBuffer
      || this.candidateBindGroupBuffers.parentIdx !== parentIdxBuffer
      || this.candidateBindGroupBuffers.pairCandidate !== pairActivityBuffer
      || this.candidateBindGroupBuffers.pairVisited !== pairVisitedBuffer
      || this.candidateBindGroupBuffers.pairActivity !== pairActivityBuffer
      || this.candidateBindGroupBuffers.shapes !== shapesBuffer
      || this.candidateBindGroupBuffers.ignoredPairHash !== ignoredPairHashBuffer
      || this.candidateBindGroupBuffers.candidateCounter !== this.candidateCounterBuffer;

    if (needsBindGroupRebuild) {
      this.candidateBindGroup = this.device.createBindGroup({
        label: 'Broadphase Candidate BindGroup',
        layout: this.candidateBindGroupLayout,
        entries: [
          { binding: 0, resource: { buffer: this.candidateUniformBuffer } },
          { binding: 1, resource: { buffer: aabbPositionBuffer } },
          { binding: 2, resource: { buffer: bvhBuffer } },
          { binding: 3, resource: { buffer: parentIdxBuffer } },
          { binding: 4, resource: { buffer: pairActivityBuffer } },
          { binding: 5, resource: { buffer: pairVisitedBuffer } },
          { binding: 6, resource: { buffer: this.candidateCounterBuffer } },
          { binding: 7, resource: { buffer: this.debugCountersBuffer } },
          { binding: 8, resource: { buffer: positionsBuffer } },
          { binding: 9, resource: { buffer: shapesBuffer } },
          { binding: 10, resource: { buffer: ignoredPairHashBuffer } },
        ],
      });
      this.candidateBindGroupBuffers.positions = positionsBuffer;
      this.candidateBindGroupBuffers.aabbPosition = aabbPositionBuffer;
      this.candidateBindGroupBuffers.bvh = bvhBuffer;
      this.candidateBindGroupBuffers.parentIdx = parentIdxBuffer;
      this.candidateBindGroupBuffers.pairCandidate = pairActivityBuffer;
      this.candidateBindGroupBuffers.pairVisited = pairVisitedBuffer;
      this.candidateBindGroupBuffers.pairActivity = pairActivityBuffer;
      this.candidateBindGroupBuffers.shapes = shapesBuffer;
      this.candidateBindGroupBuffers.ignoredPairHash = ignoredPairHashBuffer;
      this.candidateBindGroupBuffers.candidateCounter = this.candidateCounterBuffer;
    }

    if (
      !this.publishCandidateSafetyBindGroup
      || this.publishCandidateSafetyGateBuffer !== safetyGateBuffer
    ) {
      this.publishCandidateSafetyBindGroup = this.device.createBindGroup({
        label: 'Broadphase Candidate Safety Publish BindGroup',
        layout: this.publishCandidateSafetyBindGroupLayout,
        entries: [
          { binding: 0, resource: { buffer: this.candidateCounterBuffer } },
          { binding: 1, resource: { buffer: this.debugCountersBuffer } },
          { binding: 2, resource: { buffer: safetyGateBuffer } },
        ],
      });
      this.publishCandidateSafetyGateBuffer = safetyGateBuffer;
    }

    // Resolve the optional AVBD tail before starting the raw encoder. If any
    // Three r183 private surface is unavailable, broadphase still executes
    // normally and PhysicsEngine submits the complete renderer.compute()
    // fallback afterward. No kernel may be silently omitted.
    let preparedComputeTail: PreparedThreeWebGpuCompute | null = null;
    if (computeTail) {
      const prepared = prepareThreeWebGpuCompute(
        renderer,
        computeTail.commands,
        { preparationCache: this.computeTailPreparationCache },
      );
      if (prepared.ok) {
        preparedComputeTail = prepared;
      } else {
        computeTail.fallbackReason = prepared.reason;
      }
    }

    // Refit and traversal share one native encoder/submit.  This gives the
    // candidate pass same-frame visibility of sparse leaf updates and of the
    // GPU-selected full fallback without a queue round-trip or CPU readback.
    const commandEncoder = this.device.createCommandEncoder({
      label: 'Broadphase AABB + Refit + Candidate Encoder',
    });
    let aabbRecorded = !canMergeAabbWithTraversal;
    if (canMergeAabbWithTraversal) {
      aabbRecorded = this.recordUpdateAabbIntoEncoder(renderer, commandEncoder, bodyCount);
      if (!aabbRecorded) {
        // Compatibility fallback: retain the exact Three dispatch and queue
        // ordering. The ledger exposes this extra critical submit, so a future
        // backend change cannot silently violate the <=5 steady-state target.
        this.updateAabbNativeFallbacks++;
        this.submitStandaloneAabb(renderer, bodyCount, frameId);
      }
    }
    const forceFullRefit = this.activeBvhNeedsFullRefit;
    let refitRecorded = false;
    try {
      // Queue-drained telemetry may select the dense O(N) path for one
      // cadence window. It is conservative for every dirty count and halves
      // the refit wave/command train on 50k/64k motion. The expiry frame runs
      // the GPU sparse selector again, so settled scenes return automatically.
      const useDirectFullRefit = !forceFullRefit
        && frameId < this.directFullRefitUntilFrame
        && !this.topologyQualityRebuildRequested;
      if (useDirectFullRefit) {
        refitRecorded = activeBVH.recordFullRefitFromGPUBuffers?.(commandEncoder, {
          positionBuffer: aabbPositionBuffer,
          indexBuffer: this.aabbIndexBuffer,
          primCount: this.activeBvhBodyCount,
          positionStride: 3,
          sampleQuality: qualitySampleDue,
        }) ?? false;
      } else {
        refitRecorded = activeBVH.recordRefitFromGPUBuffers?.(commandEncoder, {
          positionBuffer: aabbPositionBuffer,
          indexBuffer: this.aabbIndexBuffer,
          primCount: this.activeBvhBodyCount,
          positionStride: 3,
          sampleQuality: qualitySampleDue,
          dirtyLeafBuffer,
          dirtyLeafCapacity: this.maxBodies,
          debugBuffer: this.debugCountersBuffer,
          debugCounterOffset: SPARSE_DEBUG_BASE,
          forceFullRefit,
          resetQualityBaseline: forceFullRefit,
        }) ?? false;
      }
    } catch (error) {
      if (!this.refitFailureWarned) {
        console.warn('BroadPhaseStage failed to record the same-frame BVH refit:', error);
        this.refitFailureWarned = true;
      }
    }
    if (!refitRecorded) {
      if (preparedComputeTail) {
        preparedComputeTail.rollbackAccounting();
        computeTail!.fallbackReason = 'broadphase refit could not be recorded';
      }
      // Never traverse a stale tree when the raw refit path is unavailable.
      this.candidatePairsEnabled = false;
      this.bvhBuffersReady = false;
      // The native AABB pass was recorded into the encoder we are abandoning.
      // Submit the identical Three kernel before snapshotting a recovery build
      // so topology input never lags live body state.
      if (canMergeAabbWithTraversal && aabbRecorded) {
        this.submitStandaloneAabb(renderer, bodyCount, frameId);
      }
      // Force a live full refit when recording becomes available again; the
      // next containment pass can legitimately produce zero dirty leaves.
      this.activeBvhNeedsFullRefit = true;
      this.requestTopologyRebuild('refit-fatal');
      this.activeBvhSnapshotFrame = -1;
      this.lastBuildFrame = -1;
      if (!this.buildInFlight && prewarmReady) {
        this.startBuild(aabbPositionBuffer, bodyCount, frameId, 'refit-fatal');
      }
      this.logDebugState(frameId, bodyCount, 'refitRecordFailed');
      return;
    }
    if (
      sparseSleepingMode
      && !this.recordSparseSleepingPreparation(commandEncoder, bodyCount)
    ) {
      // Every sparse resource is checked before any preparation pass is
      // recorded. If that invariant ever changes, fail open to the established
      // full-body traversal and publish no retained-prefix proof.
      sparseSleepingMode = false;
      this.sparseSelectedLast = false;
      uniforms[11] = 0;
      uniforms[12] = 0;
      uniforms[13] = 0;
      uniforms[14] = 0;
      this.device.queue.writeBuffer(this.candidateUniformBuffer, 0, uniforms);
    }
    {
      const pass = commandEncoder.beginComputePass(
        this.createComputePassDescriptor('Broadphase Candidate Pass'),
      );
      pass.setBindGroup(0, this.candidateBindGroup!);
      pass.setPipeline(candidatePipelines.clearCounter);
      pass.dispatchWorkgroups(1);
      if (clearVisitedWorkgroups > 0) {
        pass.setPipeline(candidatePipelines.clearVisited);
        pass.dispatchWorkgroups(clearVisitedWorkgroups);
      }
      if (sparseSleepingMode && this.sparseSleepingArgsBuffer) {
        pass.setPipeline(candidatePipelines.seedRetainedPairs);
        pass.dispatchWorkgroupsIndirect(
          this.sparseSleepingArgsBuffer,
          SPARSE_ARGS_RETAIN_SEED_OFFSET * 4,
        );
        pass.setPipeline(candidatePipelines.captureRetainedPrefix);
        pass.dispatchWorkgroups(1);
        pass.setPipeline(candidatePipelines.emitPairs);
        pass.dispatchWorkgroupsIndirect(
          this.sparseSleepingArgsBuffer,
          SPARSE_ARGS_AWAKE_TRAVERSAL_OFFSET * 4,
        );
      } else {
        pass.setPipeline(candidatePipelines.denseEmitPairs);
        pass.dispatchWorkgroups(Math.ceil(bodyCount / CANDIDATE_WORKGROUP_SIZE));
      }
      pass.setPipeline(
        sparseSleepingMode
          ? candidatePipelines.finalizeCounter
          : candidatePipelines.denseFinalizeCounter,
      );
      pass.dispatchWorkgroups(1);
      // A final one-thread dispatch publishes the cold retained prefix and
      // sticky fatal bit. It stays in this compute pass and command buffer, so
      // the cache-line isolation adds neither a pass transition nor a submit.
      pass.setBindGroup(0, this.publishCandidateSafetyBindGroup!);
      pass.setPipeline(candidatePipelines.publishCandidateSafety);
      pass.dispatchWorkgroups(1);
      pass.end();
    }
    if (preparedComputeTail) {
      try {
        recordPreparedThreeWebGpuCompute(
          commandEncoder,
          preparedComputeTail,
          'AVBD Solver Tail',
          {
            computePassDescriptorFactory: this.nativeComputePassDescriptorFactory,
          },
        );
        computeTail!.fused = true;
      } catch (error) {
        // The partially encoded command buffer has never been submitted.
        // Re-run the exact established broadphase path, then let the caller
        // run renderer.compute(). This is intentionally more work than a
        // silent partial advance and remains visible in the submit ledger.
        preparedComputeTail.rollbackAccounting();
        computeTail!.fallbackReason = `native tail recording failed: ${
          error instanceof Error ? error.message : String(error)
        }`;
        this.dispatch(
          renderer,
          bodyCount,
          pairCount,
          frameId,
          fixedTimeStep,
          fixedStepId,
          fixedStepSpan,
        );
        computeTail!.broadphaseSubmitted = this.candidatePairsEnabled;
        return;
      }
    }
    const fatalSampleDue = this.forceRefitFatalForTesting
      || this.forceFatalSampleReadbackTimeoutForTesting
      || this.lastScheduledFatalSampleFrame < 0
      || frameId - this.lastScheduledFatalSampleFrame >= FATAL_SAMPLE_CADENCE_FRAMES;
    // Preserve the existing backpressure accounting while a current sample is
    // pending, but do not start a fresh map on every frame once it completes.
    const fatalSampleSlot = fatalSampleDue || this.getPendingFatalSampleCount() > 0
      ? this.reserveFatalSampleSlot(frameId)
      : null;
    if (fatalSampleSlot) {
      this.lastScheduledFatalSampleFrame = frameId;
    } else if (this.forceRefitFatalForTesting) {
      // An injected/sticky fatal that collided with a pending map remains due
      // immediately after that map releases instead of waiting one cadence.
      this.lastScheduledFatalSampleFrame = -1;
    }
    if (fatalSampleSlot) {
      try {
        // These copies belong to the same encoder and submission as candidate
        // generation plus the optional AVBD/contact tail. The first word is
        // therefore the authoritative post-finalize shared safety gate. This
        // adds no queue.submit and replaces the large-scene contact poll.
        commandEncoder.copyBufferToBuffer(
          safetyGateBuffer,
          0,
          fatalSampleSlot.buffer,
          0,
          4,
        );
        commandEncoder.copyBufferToBuffer(
          this.debugCountersBuffer,
          REFIT_FATAL_COUNTER * 4,
          fatalSampleSlot.buffer,
          4,
          4,
        );
      } catch (error) {
        this.releaseFatalSampleSlot(fatalSampleSlot);
        this.latchFatalSampleInfrastructureFailure(
          'Broadphase fatal-sample copy failed; physics must fail closed.',
          error,
        );
        if (preparedComputeTail) preparedComputeTail.rollbackAccounting();
        if (computeTail) {
          computeTail.fused = false;
          computeTail.broadphaseSubmitted = false;
          computeTail.fallbackReason = `fatal-sample copy failed: ${
            error instanceof Error ? error.message : String(error)
          }`;
        }
        this.candidatePairsEnabled = false;
        return;
      }
    }
    const debugReadback = this.recordDebugReadbackCopies(
      commandEncoder,
      frameId,
      bodyCount,
      qualitySampleDue,
    );
    const submissionLabel = computeTail?.fused
      ? computeTail.combinedSubmissionLabel
      : 'broadphase-aabb-refit-candidates';
    try {
      const submit = (): void => this.device.queue.submit([commandEncoder.finish()]);
      if (this.submissionLedger) {
        this.submissionLedger.withActualSubmitScope(
          this.device.queue,
          {
            frameId,
            submissionClass: 'critical',
            label: submissionLabel,
            fixedStepId: this.currentFixedStepId,
            fixedStepSpan: this.currentFixedStepSpan,
            substepIndex: computeTail?.substepIndex,
          },
          submit,
        );
      } else {
        submit();
      }
    } catch (error) {
      if (fatalSampleSlot) this.releaseFatalSampleSlot(fatalSampleSlot);
      if (debugReadback) this.abortDebugReadback(debugReadback);
      if (preparedComputeTail) preparedComputeTail.rollbackAccounting();
      if (computeTail) {
        computeTail.fused = false;
        computeTail.broadphaseSubmitted = false;
        computeTail.fallbackReason = `combined queue submit failed: ${
          error instanceof Error ? error.message : String(error)
        }`;
      }
      this.candidatePairsEnabled = false;
      this.bvhBuffersReady = false;
      // The merged AABB/refit/candidate buffer is atomic at queue submission:
      // if submission fails, none of those mutations can be relied upon.
      // Keep traversal disabled and rebuild from live body state when the
      // device becomes usable again.
      this.activeBvhNeedsFullRefit = true;
      this.requestTopologyRebuild('refit-fatal');
      this.activeBvhSnapshotFrame = -1;
      this.lastBuildFrame = -1;
      if (!this.refitFailureWarned) {
        console.warn('BroadPhaseStage refit/candidate submit failed:', error);
        this.refitFailureWarned = true;
      }
      return;
    }
    this.forceRefitFatalForTesting = false;
    if (fatalSampleSlot) this.beginFatalSampleReadback(fatalSampleSlot);
    if (debugReadback) this.beginDebugReadback(debugReadback);
    this.submissionLedger?.record('critical', submissionLabel, {
      frameId,
      fixedStepId: this.currentFixedStepId,
      fixedStepSpan: this.currentFixedStepSpan,
      substepIndex: computeTail?.substepIndex,
    });
    if (computeTail) computeTail.broadphaseSubmitted = true;
    this.activeBvhNeedsFullRefit = false;
    this.activeBvhSnapshotFrame = frameId;
    if (qualitySampleDue) {
      this.qualitySampleCooldownFrame = frameId;
    }

    this.candidatePairsEnabled = true;

    // Queue rebuild after traversal so this frame consumes the last completed
    // BVH while the next build feeds subsequent frames.
    if (shouldStartBuild) this.pendingMaintenanceBuildReason = scheduledBuildReason;

    this.logDebugState(frameId, bodyCount, 'ready', shouldStartBuild);
  }

  hasCandidatePairs(): boolean {
    return this.candidatePairsEnabled;
  }

  hasCandidateOverflow(): boolean {
    return this.candidateOverflowDetected
      || this.fatalSampleInfrastructureFailure
      || this.getStartupPipelineError() !== null;
  }

  hasStartupPrewarmFailure(): boolean {
    return this.getStartupPipelineError() !== null;
  }

  hasFatalSampleInfrastructureFailure(): boolean {
    return this.fatalSampleInfrastructureFailure;
  }

  /** Number of submitted per-dispatch fatal samples still awaiting mapAsync. */
  getPendingFatalSampleCount(): number {
    return this.fatalSampleSlots.reduce((count, slot) => (
      count + (slot.inFlight && slot.generation === this.buildGeneration ? 1 : 0)
    ), 0);
  }

  private getTotalPendingFatalSampleCount(): number {
    return this.fatalSampleSlots.reduce(
      (count, slot) => count + (slot.inFlight ? 1 : 0),
      0,
    );
  }

  /** CPU-only; reading it records or submits no GPU work. */
  getFatalSampleDiagnostics(): BroadphaseFatalSampleDiagnostics {
    return {
      pendingSamples: this.getPendingFatalSampleCount(),
      totalPendingSamples: this.getTotalPendingFatalSampleCount(),
      backpressureSkips: this.fatalSampleBackpressureSkips,
      infrastructureFailure: this.fatalSampleInfrastructureFailure,
      lastCompletedSampleFrame: this.lastCompletedFatalSampleFrame,
    };
  }

  getLastDebugSnapshot(): BroadphaseDebugSnapshot | null {
    return this.lastDebugSnapshot ? { ...this.lastDebugSnapshot } : null;
  }

  getLifecycleSnapshot(): BroadphaseLifecycleSnapshot {
    return {
      buildStarts: this.buildStarts,
      buildPublishes: this.buildPublishes,
      buildDiscards: this.buildDiscards,
      lastBuildReason: this.lastBuildReason,
      qualityRequests: this.qualityRequests,
      qualityBuildStarts: this.qualityBuildStarts,
      activeTopologyEpoch: this.activeTopologyEpoch,
      lastQualityRatio: this.lastQualityRatio,
      lastQualitySampleFrame: this.lastQualitySampleFrame,
      buildInFlight: this.buildInFlight !== null,
      activeBodyCount: Math.max(0, this.activeBvhBodyCount),
      sleepingAabbReuseSelectedLast: this.sleepingAabbReuseSelectedLast,
      sleepingAabbReuseSelections: this.sleepingAabbReuseSelections,
    };
  }

  /**
   * Returns whether the current published topology can safely carry the next
   * solver tail in the same command buffer as its AABB/refit/candidate work.
   * An append-only delta remains exact through the bounded tail path; a shrink
   * or an oversized append must keep the established build/wait path instead.
   */
  canFuseSolverTailForBodyCount(bodyCount: number): boolean {
    const boundedBodyCount = Math.max(0, Math.floor(bodyCount));
    const appendedBodyCount = boundedBodyCount - this.activeBvhBodyCount;
    return !this.disposed
      && this.hasBuiltOnce
      && this.bvhBuffersReady
      && this.candidatePairsEnabled
      && !this.candidateOverflowDetected
      && !this.fatalSampleInfrastructureFailure
      && this.activeBvhBodyCount > 0
      && appendedBodyCount >= 0
      && appendedBodyCount <= MAX_EXACT_DELTA_BODIES;
  }

  /** CPU-only; reading it allocates no GPU resource and submits no work. */
  getPairDedupTelemetry(): BroadphasePairDedupTelemetry {
    const wordCapacity = this.pairVisitedBitsAttr.count;
    return Object.freeze({
      configured: this.pairVisitedDedupConfigured,
      wordCapacity,
      bytesPerDomain: wordCapacity * Uint32Array.BYTES_PER_ELEMENT,
      requiredWordsLast: this.pairVisitedRequiredWordsLast,
      effectiveLast: this.pairVisitedDedupEffectiveLast,
      clearWorkgroupsLast: this.pairVisitedClearWorkgroupsLast,
      frameLast: this.pairVisitedFrameLast,
    });
  }

  /** CPU-only proof of the candidate endpoint ABI selected at construction. */
  getCandidateEndpointLayout(): BroadphaseCandidateEndpointLayout {
    return this.candidateEndpointLayout;
  }

  getNativeSubmissionDiagnostics(): { ready: boolean; fallbackSubmits: number } {
    return {
      ready: this.updateAabbNativeReady,
      fallbackSubmits: this.updateAabbNativeFallbacks,
    };
  }

  /**
   * Returns only the existing Three compute nodes needed by the first
   * broadphase dispatch. Reading the list does not compile, record, or submit.
   */
  getStartupComputeNodes(): readonly any[] {
    return Object.freeze([
      this.updateAabbKernel,
      this.bootstrapKernel,
    ]);
  }

  /**
   * Waits for every configured raw pipeline family. Internal tracking catches
   * rejections to avoid an unhandled constructor promise, but this public
   * boundary always rethrows the recorded error and can never report a failed
   * compilation as ready.
   */
  async waitForStartupPrewarm(): Promise<void> {
    if (this.disposed) {
      throw new Error('Cannot wait for startup prewarm on a disposed broadphase.');
    }
    for (;;) {
      const sparsePipelinePrewarm = this.sparsePipelinePrewarm;
      await Promise.all([
        this.candidatePipelinePrewarm,
        this.lbvhPipelinePrewarm,
        sparsePipelinePrewarm ?? Promise.resolve(),
      ]);
      // attachSleepingSparseState() is single-shot, but include a stable-fence
      // check so a same-turn attachment cannot escape this wait contract.
      if (sparsePipelinePrewarm === this.sparsePipelinePrewarm) break;
    }
    const error = this.getStartupPipelineError();
    if (error) throw error;
    if (!this.isStartupPipelineReady()) {
      throw new Error(
        `BroadPhaseStage startup prewarm completed without ready state: ${
          this.getStartupPipelineWaitReason()
        }`,
      );
    }
  }

  /** CPU-only; reading it creates no WebGPU resource and submits no work. */
  getStartupPrewarmDiagnostics(): BroadphaseStartupPrewarmDiagnostics {
    const error = this.getStartupPipelineError();
    return Object.freeze({
      state: error ? 'error' : this.isStartupPipelineReady() ? 'ready' : 'pending',
      candidatePipelines: this.candidatePipelineState,
      lbvhPipelines: this.lbvhPipelineState,
      sparsePipelines: this.sparsePipelineState,
      error: error?.message ?? null,
    });
  }

  /**
   * Shared state consumed by the same-substep AVBD commit guard. Word 0 is the
   * caller-cleared fatal gate; word 1 is private broadphase counter scratch.
   */
  getSafetyGateAttribute(): StorageBufferAttribute {
    return this.safetyGateAttr;
  }

  /** Regression-only hook: the next candidate dispatch observes a refit fault. */
  injectRefitFatalForTesting(): void {
    if (this.disposed) throw new Error('Cannot inject a fatal into a disposed broadphase.');
    this.forceRefitFatalForTesting = true;
  }

  /** Regression-only: keep the next real map pending past the watchdog. */
  injectFatalSampleReadbackTimeoutForTesting(): void {
    if (this.disposed) throw new Error('Cannot inject a timeout into a disposed broadphase.');
    this.forceFatalSampleReadbackTimeoutForTesting = true;
  }

  /**
   * Copies diagnostics after all broadphase work submitted before this call.
   * The dedicated staging buffer avoids racing the periodic telemetry map.
   * Counters 0/9/10/11 are scene-cumulative, so this is suitable for final
   * regression assertions even if a topology rebuild occurred meanwhile.
   */
  async readDebugSnapshotAsync(): Promise<BroadphaseDebugSnapshot | null> {
    const bodyCount = this.requestedBodyCount;
    if (bodyCount < 0) {
      return this.getLastDebugSnapshot();
    }

    const generation = this.buildGeneration;
    const topologyEpoch = this.activeTopologyEpoch;
    const sequence = this.debugReadbackSequence++;
    const readbackBuffer = this.createDebugReadbackBuffer();
    let mapped = false;
    try {
      const encoder = this.device.createCommandEncoder({ label: 'Broadphase Final Diagnostics Readback' });
      const qualityMetricsBuffer = this.getActiveBVH().qualityMetricsBuffer;
      if (qualityMetricsBuffer) {
        encoder.copyBufferToBuffer(
          qualityMetricsBuffer,
          2 * 4,
          this.debugCountersBuffer,
          QUALITY_RATIO_COUNTER * 4,
          4 * 4,
        );
      }
      encoder.copyBufferToBuffer(
        this.debugCountersBuffer,
        0,
        readbackBuffer,
        0,
        DEBUG_COUNTER_WORDS * 4,
      );
      // This API is an explicit queue-drained inspection cycle, normally used
      // by regression tooling after simulation has stopped. It is not owned by
      // the last animation frame and must not be back-attributed into that
      // frame's 2..5 production submission budget.
      this.device.queue.submit([encoder.finish()]);
      await readbackBuffer.mapAsync(GPUMapMode.READ);
      mapped = true;
      if (generation !== this.buildGeneration) {
        return null;
      }
      const values = new Uint32Array(readbackBuffer.getMappedRange().slice(0));
      const snapshot = this.makeDebugSnapshot(values, this.lastDispatchFrame, bodyCount);
      this.applyDebugSnapshot(snapshot, sequence, topologyEpoch);
      return { ...snapshot };
    } finally {
      if (mapped) {
        readbackBuffer.unmap();
      }
      readbackBuffer.destroy();
    }
  }

  setDebugEnabled(enabled: boolean): void {
    this.debugEnabled = enabled;
    // Do not clear debugReadbackInFlight here. mapAsync cannot be cancelled,
    // and permitting another map on the same readback buffer would make the
    // two generations race each other's unmap/finally handlers.
    this.lastDebugLogFrame = -1;
  }

  setDebugLogInterval(intervalFrames: number): void {
    this.debugEveryNFrames = Math.max(1, Math.floor(intervalFrames));
    this.lastDebugLogFrame = -1;
  }

  isReady(): boolean {
    return this.getStartupPipelineError() === null
      && this.isStartupPipelineReady()
      && this.backendBuffersReady
      && this.bvhBuffersReady;
  }

  getLastBuildMs(): number {
    return this.lastBuildMs;
  }

  reset(): void {
    if (this.disposed) { return; }
    this.buildGeneration++;
    // An old generation cannot be cancelled, so retain its promise as a
    // serialization fence. Its completion is ignored by buildGeneration and
    // the guarded finally handler clears the fence before a new build starts.
    if (!this.buildInFlight) {
      this.buildInFlightStartFrame = -1;
      this.buildInFlightBodyCount = -1;
    }
    this.activeBvhIndex = 0;
    this.activeBvhBodyCount = -1;
    this.activeBvhSnapshotFrame = -1;
    this.activeBvhNeedsFullRefit = false;
    this.latestAabbPositionBuffer = null;
    this.requestedBodyCount = -1;
    this.lastBuildMs = 0;
    this.candidatePairsEnabled = false;
    this.backendBuffersReady = false;
    this.bvhBuffersReady = false;
    this.storageBuffersInitialized = false;
    this.storageInitWarned = false;
    this.bootstrapDone = false;
    this.lastBuildFrame = -1;
    this.hasBuiltOnce = false;
    this.refitFailureWarned = false;
    this.candidateOverflowDetected = false;
    this.fatalSampleInfrastructureFailure = false;
    this.fatalSampleFailureWarned = false;
    this.fatalSampleBackpressureSkips = 0;
    this.lastCompletedFatalSampleFrame = -1;
    this.lastScheduledFatalSampleFrame = -1;
    this.lastDebugSnapshot = null;
    this.topologyQualityRebuildRequested = false;
    this.pendingRebuildReason = null;
    this.pendingMaintenanceBuildReason = null;
    this.activeTopologyEpoch = 0;
    this.debugReadbackSequence = 0;
    this.lastAppliedDebugReadbackSequence = -1;
    this.lastDispatchFrame = -1;
    this.qualitySampleCooldownFrame = -1;
    this.lastQualitySampleFrame = -1;
    this.lastQualityRatio = 1.0;
    this.lastObservedQualitySamples = 0;
    this.directFullRefitUntilFrame = -1;
    this.fatInitializedBodyCount = 0;
    this.updateAabbNativeFallbacks = 0;
    this.currentFixedStepId = null;
    this.currentFixedStepSpan = 1;
    this.forceRefitFatalForTesting = false;
    this.forceFatalSampleReadbackTimeoutForTesting = false;
    this.pairVisitedRequiredWordsLast = 0;
    this.pairVisitedDedupEffectiveLast = false;
    this.pairVisitedClearWorkgroupsLast = 0;
    this.pairVisitedFrameLast = -1;
    this.sparseSelectedLast = false;
    this.sparseSelectedDispatches = 0;
    this.sparseCertificateGenerationLast = -1;
    this.sparseSleepingBindGroup = null;
    this.sparseSleepingWorkBindGroup = null;
    this.sparseSleepingBindGroupBuffers = null;
    this.device.queue.writeBuffer(
      this.debugCountersBuffer,
      0,
      new Uint32Array(DEBUG_COUNTER_WORDS),
    );
    this.ignoredPairHashUploadedVersion = -1;
    this.safetyGateUploadedVersion = -1;
    // mapAsync cannot be cancelled. Rotate the staging buffer when a reset
    // happens mid-readback so the new scene is not held behind old GPU work,
    // while each individual GPUBuffer still has at most one pending map. The
    // stale buffer is retired by its own finally handler below.
    if (this.debugReadbackInFlight) {
      this.debugReadbackBuffer = this.createDebugReadbackBuffer();
      this.debugReadbackInFlight = false;
    }
    this.lastDebugLogFrame = -1;
    this.candidateBindGroup = null;
    this.candidateBindGroupBuffers.positions = null;
    this.candidateBindGroupBuffers.aabbPosition = null;
    this.candidateBindGroupBuffers.bvh = null;
    this.candidateBindGroupBuffers.parentIdx = null;
    this.candidateBindGroupBuffers.pairCandidate = null;
    this.candidateBindGroupBuffers.pairVisited = null;
    this.candidateBindGroupBuffers.pairActivity = null;
    this.candidateBindGroupBuffers.shapes = null;
    this.candidateBindGroupBuffers.ignoredPairHash = null;
    this.candidateBindGroupBuffers.candidateCounter = null;
    this.publishCandidateSafetyBindGroup = null;
    this.publishCandidateSafetyGateBuffer = null;
  }

  dispose(): void {
    if (this.disposed) { return; }
    this.disposed = true;
    this.buildGeneration++;
    this.candidatePairsEnabled = false;
    this.backendBuffersReady = false;
    this.bvhBuffersReady = false;
    this.latestAabbPositionBuffer = null;
    this.candidateBindGroup = null;
    this.aabbSnapshotBuffer.destroy();
    this.aabbIndexBuffer.destroy();
    this.candidateUniformBuffer.destroy();
    this.candidateCounterBuffer.destroy();
    this.sparseSleepingUniformBuffer?.destroy();
    this.sparseSleepingArgsBuffer?.destroy();
    this.sparseSleepingUniformBuffer = null;
    this.sparseSleepingArgsBuffer = null;
    this.sparseSleepingBindGroup = null;
    this.sparseSleepingWorkBindGroup = null;
    this.sparseSleepingBindGroupBuffers = null;
    this.debugCountersBuffer.destroy();
    this.debugReadbackBuffer.destroy();
    for (const slot of this.fatalSampleSlots) {
      if (slot.inFlight) {
        // mapAsync cannot be cancelled. Its finally handler owns destruction
        // after the mapping lifecycle has completed.
        slot.destroyWhenComplete = true;
      } else {
        slot.destroyWhenComplete = true;
        slot.buffer.destroy();
      }
    }
    this.gpuBVHs[0].dispose();
    this.gpuBVHs[1].dispose();
  }

  private makeDebugSnapshot(
    values: Uint32Array,
    frameId: number,
    bodyCount: number,
  ): BroadphaseDebugSnapshot {
    const emittedCount = values[5] ?? 0;
    const globalCapacity = Math.min(this.maxPairs, bodyCount * this.maxPairsPerBody);
    return {
      frame: frameId,
      bodies: bodyCount,
      candidates: emittedCount,
      capacity: globalCapacity,
      capacityDrops: Math.max(values[0] ?? 0, Math.max(0, emittedCount - globalCapacity)),
      dedupDrops: values[1] ?? 0,
      writes: values[2] ?? 0,
      leafCandidates: values[4] ?? 0,
      visitBudgetDrops: values[9] ?? values[3] ?? 0,
      stackDrops: values[10] ?? values[7] ?? 0,
      epochVisitBudgetDrops: values[3] ?? 0,
      epochStackDrops: values[7] ?? 0,
      ignoredPairProbeFallbacks: values[11] ?? 0,
      pairsPerBody: this.maxPairsPerBody,
      topologyQualityRatio: Math.max(
        0,
        (values[QUALITY_RATIO_COUNTER] ?? QUALITY_RATIO_SCALE) / QUALITY_RATIO_SCALE,
      ),
      topologyQualitySamples: values[QUALITY_SAMPLES_COUNTER] ?? 0,
      topologyQualityFault: (values[QUALITY_FAULT_COUNTER] ?? 0) !== 0,
      topologyQualityFaults: values[QUALITY_FAULTS_COUNTER] ?? 0,
      dirtyLeaves: values[SPARSE_DIRTY_LEAVES_COUNTER] ?? 0,
      dirtyAncestors: values[SPARSE_DIRTY_ANCESTORS_COUNTER] ?? 0,
      containmentHits: values[SPARSE_CONTAINMENT_HITS_COUNTER] ?? 0,
      containmentMisses: values[SPARSE_CONTAINMENT_MISSES_COUNTER] ?? 0,
      sparseRefitFrames: values[SPARSE_SUCCESS_FRAMES_COUNTER] ?? 0,
      fullRefitFrames: values[FULL_FALLBACK_FRAMES_COUNTER] ?? 0,
      refitFatal: (values[REFIT_FATAL_COUNTER] ?? 0) !== 0,
      refitFatalFrames: values[REFIT_FATAL_CUMULATIVE_COUNTER] ?? 0,
      forcedFullRefits: values[FORCED_FULL_COUNTER] ?? 0,
      denseFullRefits: values[DENSE_FULL_COUNTER] ?? 0,
      sparseInputFaults: values[SPARSE_INPUT_FAULT_COUNTER] ?? 0,
      postFullValidationFaults: values[POST_FULL_VALIDATION_FAULT_COUNTER] ?? 0,
      fatalCumulative: (values[FATAL_CUMULATIVE_COUNTER] ?? 0) !== 0,
      sleepingAabbSkippedBodies: values[SLEEPING_AABB_SKIPPED_COUNTER] ?? 0,
    };
  }

  private applyDebugSnapshot(
    snapshot: BroadphaseDebugSnapshot,
    sequence: number,
    topologyEpoch: number,
  ): void {
    // Historical capacity loss remains sticky even when an older telemetry
    // callback completes after a newer one.
    this.candidateOverflowDetected ||= snapshot.capacityDrops > 0;
    if (sequence >= this.lastAppliedDebugReadbackSequence) {
      this.lastAppliedDebugReadbackSequence = sequence;
      this.lastDebugSnapshot = snapshot;
    }
    // Only diagnostics sampled from the topology that is still active may
    // request its replacement. A delayed old-epoch callback remains visible
    // through cumulative counters but cannot re-trigger a rebuild.
    if (topologyEpoch === this.activeTopologyEpoch) {
      if (snapshot.topologyQualitySamples > this.lastObservedQualitySamples) {
        this.lastObservedQualitySamples = snapshot.topologyQualitySamples;
        this.lastQualityRatio = snapshot.topologyQualityRatio;
        this.lastQualitySampleFrame = snapshot.frame;
      }
      const denseLimit = Math.min(
        snapshot.bodies,
        Math.max(64, Math.min(4096, Math.max(1, Math.floor(snapshot.bodies / 8)))),
      );
      if (
        !snapshot.refitFatal
        && !snapshot.topologyQualityFault
        && snapshot.dirtyLeaves > denseLimit
      ) {
        this.directFullRefitUntilFrame = snapshot.frame + this.debugEveryNFrames;
      } else {
        this.directFullRefitUntilFrame = -1;
      }
      if (snapshot.refitFatal) {
        // A latched invalid topology must bypass the quality cooldown; waiting
        // hundreds of 64K frames would keep the candidate stream unavailable.
        this.lastBuildFrame = -1;
      }
      const qualityRebuildRequested = snapshot.epochVisitBudgetDrops > 0
        || snapshot.epochStackDrops > 0
        || snapshot.topologyQualityFault
        || (
          snapshot.topologyQualitySamples > 0
          && snapshot.topologyQualityRatio >= QUALITY_REBUILD_THRESHOLD
        );
      if (snapshot.refitFatal) {
        this.requestTopologyRebuild('refit-fatal');
      } else if (qualityRebuildRequested) {
        this.requestTopologyRebuild('quality');
      }
    }
  }

  private recordDebugReadbackCopies(
    commandEncoder: GPUCommandEncoder,
    frameId: number,
    bodyCount: number,
    qualitySampleDue: boolean,
  ): BroadphaseDebugReadbackContext | null {
    // Fatal/capacity correctness is sampled submit-free by the fused ring.
    // Production only needs this larger mapped snapshot when the GPU actually
    // produced a new topology-quality sample; debug mode retains its requested
    // cadence. Its copies are appended to the already-owned candidate encoder,
    // so quality sampling cannot become a sixth queue submission.
    if (!this.debugEnabled && !qualitySampleDue) return null;
    if (this.candidateOverflowDetected && !this.debugEnabled) return null;
    if (this.debugReadbackInFlight) return null;
    if (
      this.lastDebugLogFrame >= 0
      && frameId - this.lastDebugLogFrame < this.debugEveryNFrames
    ) return null;

    this.debugReadbackInFlight = true;
    this.lastDebugLogFrame = frameId;
    const context: BroadphaseDebugReadbackContext = {
      frameId,
      bodyCount,
      generation: this.buildGeneration,
      topologyEpoch: this.activeTopologyEpoch,
      sequence: this.debugReadbackSequence++,
      buffer: this.debugReadbackBuffer,
    };
    try {
      const qualityMetricsBuffer = this.getActiveBVH().qualityMetricsBuffer;
      if (qualityMetricsBuffer) {
        commandEncoder.copyBufferToBuffer(
          qualityMetricsBuffer,
          2 * 4,
          this.debugCountersBuffer,
          QUALITY_RATIO_COUNTER * 4,
          4 * 4,
        );
      }
      commandEncoder.copyBufferToBuffer(
        this.debugCountersBuffer,
        0,
        context.buffer,
        0,
        DEBUG_COUNTER_WORDS * 4,
      );
      return context;
    } catch (error) {
      this.abortDebugReadback(context);
      console.warn('Broadphase debug readback copy failed:', error);
      return null;
    }
  }

  private abortDebugReadback(context: BroadphaseDebugReadbackContext): void {
    if (context.buffer === this.debugReadbackBuffer) {
      this.debugReadbackInFlight = false;
    } else {
      context.buffer.destroy();
    }
  }

  private beginDebugReadback(context: BroadphaseDebugReadbackContext): void {
    let mapPromise: Promise<void>;
    try {
      mapPromise = context.buffer.mapAsync(GPUMapMode.READ);
    } catch (error) {
      this.abortDebugReadback(context);
      console.warn('Broadphase debug readback map failed synchronously:', error);
      return;
    }
    void mapPromise.then(() => {
      try {
        const mapped = context.buffer.getMappedRange();
        const values = new Uint32Array(mapped.slice(0));
        // reset() invalidates telemetry from the old scene. Always complete
        // the map/unmap lifecycle, but never let that stale result latch an
        // overflow (or emit a misleading log) into the new scene.
        if (context.generation !== this.buildGeneration) {
          return;
        }
        const dispatchSpan = values[6] ?? 0;
        const snapshot = this.makeDebugSnapshot(
          values,
          context.frameId,
          context.bodyCount,
        );
        this.applyDebugSnapshot(snapshot, context.sequence, context.topologyEpoch);
        const topologyDegraded = snapshot.epochVisitBudgetDrops > 0
          || snapshot.epochStackDrops > 0;
        const overflow = snapshot.capacityDrops > 0 || topologyDegraded;
        // Only a true global candidate-capacity loss is a sticky correctness
        // failure. Visit/stack diagnostics remain observable but do not latch
        // PhysicsEngine before a later periodic rebuild can refresh topology;
        // the old latch created a permanent freeze with no recovery path.
        if (this.debugEnabled || overflow) {
          const log = overflow ? console.error : console.info;
          log(
            `[Broadphase ${overflow ? 'Overflow' : 'Debug'}] frame=${snapshot.frame} bodies=${snapshot.bodies} ` +
            `candidates=${snapshot.candidates} capacityDrops=${snapshot.capacityDrops} ` +
            `dedupDrops=${snapshot.dedupDrops} writes=${snapshot.writes} ` +
            `leafCandidates=${snapshot.leafCandidates} visitBudgetDrops=${snapshot.visitBudgetDrops} ` +
            `stackDrops=${snapshot.stackDrops} epochVisitDrops=${snapshot.epochVisitBudgetDrops} ` +
            `epochStackDrops=${snapshot.epochStackDrops} ignoredProbeFallbacks=${snapshot.ignoredPairProbeFallbacks} ` +
            `dirtyLeaves=${snapshot.dirtyLeaves} dirtyAncestors=${snapshot.dirtyAncestors} ` +
            `containment=${snapshot.containmentHits}/${snapshot.containmentMisses} ` +
            `sparseFrames=${snapshot.sparseRefitFrames} fullFrames=${snapshot.fullRefitFrames} ` +
            `refitFatal=${snapshot.refitFatal ? 1 : 0} denseFull=${snapshot.denseFullRefits} ` +
            `globalCapacity=${snapshot.capacity} ` +
            `capacityHintPerBody=${snapshot.pairsPerBody} dispatchSpan=${dispatchSpan}`,
          );
        }
      } finally {
        context.buffer.unmap();
      }
    }).catch((error) => {
      if (context.generation === this.buildGeneration) {
        console.warn('Broadphase debug readback failed:', error);
      }
    }).finally(() => {
      if (context.buffer === this.debugReadbackBuffer) {
        this.debugReadbackInFlight = false;
      } else {
        context.buffer.destroy();
      }
    });
  }

  private logDebugState(
    frameId: number,
    bodyCount: number,
    reason: string,
    shouldStartBuild?: boolean,
  ): void {
    if (!this.debugEnabled) return;
    const activeAgeFrames =
      this.activeBvhSnapshotFrame >= 0 ? Math.max(0, frameId - this.activeBvhSnapshotFrame) : -1;
    const inflightAgeFrames =
      this.buildInFlight && this.buildInFlightStartFrame >= 0
        ? Math.max(0, frameId - this.buildInFlightStartFrame)
        : -1;
    const shouldBuildToken = shouldStartBuild === undefined ? '' : ` shouldStartBuild=${shouldStartBuild ? 1 : 0}`;
    console.info(
      `[Broadphase State] frame=${frameId} bodies=${bodyCount} reason=${reason} ` +
      `hasBuiltOnce=${this.hasBuiltOnce ? 1 : 0} buildInFlight=${this.buildInFlight ? 1 : 0} ` +
      `activeBvhBodyCount=${this.activeBvhBodyCount} requestedBodyCount=${this.requestedBodyCount} ` +
      `bvhBuffersReady=${this.bvhBuffersReady ? 1 : 0} activeBvhIndex=${this.activeBvhIndex} ` +
      `activeBvhSnapshotFrame=${this.activeBvhSnapshotFrame} activeBvhAge=${activeAgeFrames} ` +
      `buildInFlightBodyCount=${this.buildInFlightBodyCount} ` +
      `buildInFlightStartFrame=${this.buildInFlightStartFrame} buildInFlightAge=${inflightAgeFrames} ` +
      `lastBuildMs=${this.lastBuildMs.toFixed(3)} ` +
      `prewarmInFlight=${this.prewarmInFlight ? 1 : 0} ` +
      `candidatePipelineState=${this.candidatePipelineState} ` +
      `lbvhPipelineState=${this.lbvhPipelineState} ` +
      `sparsePipelineState=${this.sparsePipelineState} ` +
      `backendReady=${this.backendBuffersReady ? 1 : 0} candidatePairsEnabled=${this.candidatePairsEnabled ? 1 : 0}` +
      shouldBuildToken,
    );
  }
}
